import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getTagColor } from '../constants/tags';
import ActivityChart from '../components/ActivityChart';
// Phase 11 slot 12 (§8.5 row 2 / PRD-DATA-001): removed fallbackJobs /
// fallbackAssessments / fallbackTransactions imports. The DataProvider
// guarantees `dataSource === 'supabase'` in production (`'error'` if
// Supabase is down — never `'fallback'` in prod). Rendering pre-backbone
// static rows behind a real customer's data was a confusing UX trap when
// a fetch failed silently. The page now shows empty / loading / error
// states off real data only.
import { stageConfig } from '../data/jobs';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useGodMode } from '../context/GodModeContext';
import GodModeTooltip from '../components/GodModeTooltip';
import { useData } from '../context/DataProvider';
import { fetchBrowseJobs, fetchMyApplications, fetchAssessments, fetchPayments, fetchReferrals, fetchCurrentJobsWithTimeEntries, fetchSavedJobs } from '../adapters/jobAdapter';
import { logHours as logTimeEntry, fetchEntriesForPerson } from '../services/timeEntryService';
import { checkQualifications, getQualifiedDomains, getSkillsEvalDomains } from '../utils/qualificationCheck';
import { fetchBatchesForAnnotator } from '../services/batchService';
import { BATCH_STATES } from '../utils/batchProgress';
import useOnboardingSteps from '../hooks/useOnboardingSteps';
import { useTimeframe } from '../hooks/useTimeframe';
import TimeframeSelector from '../components/TimeframeSelector';
import { getOnboardingDisplayState, getOnboardingStepStatus } from '../services/onboardingService';

const TABS = ['Browse Jobs', 'My Jobs', 'My Applications', 'Saved Jobs', 'Assessments', 'Payments', 'Referrals'];
const DOMAINS = ['All', 'Audio', 'Video', 'Visual', 'Linguistics', 'Music', 'Data Science', 'AI Evaluation', 'Computer Use'];
const SORTS = ['Recommended', 'Newest', 'Highest Pay', 'Closing Soon'];
const TIER_COLORS = { Standard: 'var(--text-muted)', Advanced: 'var(--accent-teal)', Expert: 'var(--accent-gold)' };
const SAVED_JOB_STATUS_META = {
  active: {
    label: 'In Progress',
    color: 'var(--accent-gold)',
    background: 'rgba(var(--accent-gold-rgb), 0.10)',
    border: 'rgba(var(--accent-gold-rgb), 0.25)',
    note: 'This project is already underway and may no longer be accepting new annotators.',
  },
  completed: {
    label: 'Completed',
    color: 'var(--accent-teal)',
    background: 'rgba(var(--accent-teal-rgb), 0.10)',
    border: 'rgba(var(--accent-teal-rgb), 0.25)',
    note: 'This saved job is historical only. It is no longer open for application.',
  },
  cancelled: {
    label: 'Cancelled',
    color: "var(--text-error)",
    background: 'var(--bg-error)',
    border: 'var(--border-error)',
    note: 'This project was cancelled and is kept only as saved history.',
  },
  draft: {
    label: 'Draft',
    color: 'var(--text-muted)',
    background: 'rgba(255,255,255,0.04)',
    border: 'rgba(255,255,255,0.10)',
    note: 'This project is not published yet, so saved access is preview-only.',
  },
};

function mergeApplications(localApps = [], liveApps = []) {
  const orderedJobIds = [];
  const orderedSet = new Set();
  const merged = new Map();

  const remember = (jobId) => {
    if (!jobId || orderedSet.has(jobId)) return;
    orderedSet.add(jobId);
    orderedJobIds.push(jobId);
  };

  for (const app of liveApps) remember(app?.jobId);
  for (const app of localApps) remember(app?.jobId);

  for (const app of localApps) {
    if (!app?.jobId) continue;
    merged.set(app.jobId, app);
  }

  for (const app of liveApps) {
    if (!app?.jobId) continue;
    merged.set(app.jobId, { ...(merged.get(app.jobId) || {}), ...app });
  }

  return orderedJobIds.map(jobId => merged.get(jobId)).filter(Boolean);
}

function buildAssignmentBackedApplication(assignment, application) {
  const job = assignment?.job || {};
  return {
    id: application?.id || assignment?.id || assignment?.jobId,
    jobId: assignment?.jobId,
    jobTitle: application?.jobTitle || job.title || 'Unknown Job',
    domain: application?.domain || job.domain || '',
    tier: application?.tier || job.tier || 'Standard',
    // R11 MED-4 audit anchor: `assignment.rate` is canonical (post-R11 jobAdapter
    // fix, resolveAssignmentRate). `application.rate` is the at-apply-time rate,
    // preferred when present. `job.rate` is the posting-level default fallback.
    rate: application?.rate ?? assignment?.rate ?? job.rate ?? 0,
    client: application?.client || job.client || '',
    teamSize: application?.teamSize ?? job.teamSize ?? 0,
    currentStaffed: application?.currentStaffed ?? job.currentStaffed ?? 0,
    overview: application?.overview || job.overview || '',
    appliedDate: application?.appliedDate || '',
    estimatedResponse: application?.estimatedResponse,
    stage: assignment?.status === 'Active' ? 'started' : assignment?.status === 'Completed' ? 'completed' : 'accepted',
    stageLabel: application?.stageLabel || assignment?.status || 'Accepted',
    source: application?.source,
    decision: application?.decision,
    resumeSubmitted: application?.resumeSubmitted,
    domainAssessmentPassed: application?.domainAssessmentPassed,
    skillsEvaluationPassed: application?.skillsEvaluationPassed,
    workAuthVerified: application?.workAuthVerified,
    completedSteps: application?.completedSteps,
    assignmentStatus: assignment?.status || null,
    payConfirmed: assignment?.payConfirmed || false,
    // R11 MED-4 audit fix: removed `confirmedRate` alias. The only consumer
    // (onboarding rate display) now reads `rate` directly — the canonical
    // field set above. One rate field = one source of truth.
  };
}

const ROUTE_TAB_MAP = {
  '/find-work': 'Browse Jobs',
  '/my-work': 'My Jobs',
  '/assessments': 'Assessments',
  '/earnings': 'Payments',
};

const ROUTE_TITLES = {
  '/find-work': 'Find Work',
  '/my-work': 'My Work',
  '/assessments': 'Assessments',
  '/earnings': 'Earnings',
};

const ROUTE_SUBTABS = {
  '/my-work': ['My Jobs', 'My Applications', 'Saved Jobs'],
  '/earnings': ['Payments', 'Referrals'],
};

export default function Jobs() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeTab = ROUTE_TAB_MAP[location.pathname];
  const isAnnotatorRoute = !!routeTab;
  const routeTitle = ROUTE_TITLES[location.pathname];
  const routeSubtabs = ROUTE_SUBTABS[location.pathname];
  const [activeTab, setActiveTab] = useState(routeTab || 'Browse Jobs');
  const [activeDomain, setActiveDomain] = useState('All');
  const [activeSort, setActiveSort] = useState('Recommended');
  const [searchQuery, setSearchQuery] = useState('');
  const [quickApplyConfirm, setQuickApplyConfirm] = useState(null);
  const [justApplied, setJustApplied] = useState(new Set()); // Jobs applied to THIS session — show confirmation card
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [preApplySearch, setPreApplySearch] = useState('');
  const [minRate, setMinRate] = useState(0);
  const [maxRate, setMaxRate] = useState(9999);
  // completedSteps defined after assessments load — see below line ~82
  const { activeBoosts, appliedJobs, acceptedJobs, startedJobs, startJob, savedJobs, applications, applyToJob, toggleSaveJob, onboardingChecklists } = useApp();
  const hasRateBoost = activeBoosts.has('rate_boost');
  const { dataSource, getConfig, currentUser, currentUserId } = useData();
  const { isGodMode, effectiveUserId } = useAuth();
  const { hoveredEntity } = useGodMode();
  const { steps: templateOnboardingSteps } = useOnboardingSteps();
  const getOnboardingState = (progressData, job = null) => getOnboardingDisplayState(templateOnboardingSteps, progressData, job);

  // Sync active tab when route changes (annotator view)
  useEffect(() => {
    if (routeTab && activeTab !== routeTab && !routeSubtabs?.includes(activeTab)) {
      setActiveTab(routeTab);
    }
  }, [location.pathname]);

  // ===== SUPABASE DATA FETCHING =====
  const [dbJobs, setDbJobs] = useState(null);
  const [dbCurrentJobs, setDbCurrentJobs] = useState(null);
  const [todayEntries, setTodayEntries] = useState({});
  const [weekEntries, setWeekEntries] = useState({});
  const [hourInputs, setHourInputs] = useState({});
  const [logStatus, setLogStatus] = useState({});
  const [dbMyApplications, setDbMyApplications] = useState(null);
  const [dbSavedJobs, setDbSavedJobs] = useState(null);
  const [dbAssessments, setDbAssessments] = useState(null);
  const [dbPayments, setDbPayments] = useState(null);
  // Earnings timeframe — now uses the shared useTimeframe hook (same component
  // + state shape that BossDashboard, SPLDashboard, AdminFinancials, Analytics
  // use). dateRange is { start, end } or null for "all time".
  const {
    preset: earningsPreset,
    setPreset: setEarningsPreset,
    dateRange: earningsDateRange,
    customStart: earningsCustomStart,
    setCustomStart: setEarningsCustomStart,
    customEnd: earningsCustomEnd,
    setCustomEnd: setEarningsCustomEnd,
  } = useTimeframe('this_week');
  const [periodEntries, setPeriodEntries] = useState(null); // null = use weekEntries (this_week default)
  const [dbReferrals, setDbReferrals] = useState(null);
  const [_dbLoading, setDbLoading] = useState(true);
  const [userBatches, setUserBatches] = useState([]);
  const savedJobCount = savedJobs.size;

  // Batch fetching for embedded batch queues
  const loadBatches = async () => {
    if (!effectiveUserId) return;
    const data = await fetchBatchesForAnnotator(effectiveUserId);
    setUserBatches(data || []);
  };
  useEffect(() => { if (dataSource === 'supabase' && effectiveUserId) loadBatches(); }, [dataSource, effectiveUserId]);

  // Fetch time entries for the selected earnings period. For 'this_week' and
  // 'all_time' we fall back to the already-loaded weekEntries (from jobAdapter)
  // — no separate query needed. For other presets we refetch with the
  // dateRange the useTimeframe hook computed.
  useEffect(() => {
    if (!effectiveUserId) { setPeriodEntries(null); return; }
    // this_week + all_time reuse the pre-loaded weekEntries to avoid a duplicate
    // query on first load. For all_time we also want to show lifetime entries
    // though — but the KPI section above already computes lifetime from
    // assignments, so weekEntries is OK here (used only for period-scoped KPIs).
    if (earningsPreset === 'this_week' || earningsPreset === 'all_time') {
      setPeriodEntries(null);
      return;
    }
    if (!earningsDateRange) { setPeriodEntries(null); return; }
    fetchEntriesForPerson(effectiveUserId, {
      startDate: earningsDateRange.start,
      endDate: earningsDateRange.end,
    }).then(data => setPeriodEntries(data || []))
      .catch(() => setPeriodEntries([]));
  }, [effectiveUserId, earningsPreset, earningsDateRange]);

  // Group batches by job_id
  const batchesByJob = {};
  userBatches.forEach(b => {
    const key = b.job_id || 'unknown';
    if (!batchesByJob[key]) batchesByJob[key] = [];
    batchesByJob[key].push(b);
  });

  useEffect(() => {
    if (dataSource !== 'supabase') {
      setDbLoading(false);
      return;
    }
    async function loadJobsData() {
      try {
        const [jobs, currentJobs, myApplications, saved, assessData, paymentsData, referralData] = await Promise.all([
          fetchBrowseJobs(),
          fetchCurrentJobsWithTimeEntries(),
          fetchMyApplications(),
          fetchSavedJobs(),
          fetchAssessments(),
          fetchPayments(),
          fetchReferrals(),
        ]);
        setDbJobs(jobs);
        setDbCurrentJobs(currentJobs);
        if (currentJobs.todayEntries) setTodayEntries(currentJobs.todayEntries);
        if (currentJobs.weekEntries) setWeekEntries(currentJobs.weekEntries);
        // Pre-fill inputs from today's entries
        const prefill = {};
        for (const [aId, entry] of Object.entries(currentJobs.todayEntries || {})) {
          prefill[aId] = String(entry.hours);
        }
        setHourInputs(prefill);
        setDbMyApplications(myApplications);
        setDbSavedJobs(saved);
        setDbAssessments(assessData.all);
        setDbPayments(paymentsData);
        setDbReferrals(referralData);
      } catch (err) {
        console.warn('Jobs fetch failed, using fallback:', err);
      } finally {
        setDbLoading(false);
      }
    }
    loadJobsData();
  }, [dataSource, currentUserId]);

  // Phase 11 slot 12 (§8.5 row 2): empty array on null instead of static
  // fallback. UI loading/empty states branch off `_dbLoading` and length=0.
  const assessments = dbAssessments || [];
  const transactions = dbPayments?.transactions || [];

  // Application readiness — derived from DB assessment results
  // Cross-domain aware: tracks ALL domains the user has valid assessments for
  const qualifiedDomains = getQualifiedDomains(assessments);
  const skillsEvalDomains = getSkillsEvalDomains(assessments);
  const userDomain = currentUser?.domain || currentUser?.primary_domain || 'Audio';
  const passedDomainAssessment = qualifiedDomains.has(userDomain);
  const passedSkillsEval = skillsEvalDomains.has(userDomain);
  // Global completedSteps for user's primary domain (used as default)
  const completedSteps = {
    resume: !!(currentUser?.resume_url || currentUser?.resumeUrl),
    domainAssessment: passedDomainAssessment,
    skillsEvaluation: passedSkillsEval,
    workAuth: (currentUser?.work_auth_status || currentUser?.workAuthStatus) === 'Verified',
  };
  // Per-job qualification check (used by detail panel for cross-domain support)
  const getJobQualification = (job) => checkQualifications({ job, user: currentUser, assessments, requiredAssessments: job?.requiredAssessments || [] });

  // Phase 11 slot 12 (§8.5 row 2): empty array on null instead of static
  // fallback. Browse-jobs UI shows the empty/loading state when no jobs
  // are returned — do NOT render hardcoded pre-backbone listings to a
  // real customer.
  const allJobs = dbJobs || [];

  const workerApplications = dataSource === 'supabase'
    ? mergeApplications(applications, dbMyApplications || [])
    : applications;
  const currentAssignments = dbCurrentJobs || { onboarding: [], active: [], completed: [] };
  const onboardingAssignments = currentAssignments.onboarding || [];
  const activeAssignments = currentAssignments.active || [];
  const completedAssignments = currentAssignments.completed || [];
  const activeJobIds = new Set(activeAssignments.map(a => a.jobId));
  // Jobs with any assignment (active/onboarding/completed) — these should not show as pending applications
  const assignedJobIds = new Set([
    ...activeAssignments.map(a => a.jobId),
    ...onboardingAssignments.map(a => a.jobId),
    ...completedAssignments.map(a => a.jobId),
  ].filter(Boolean));
  const completedJobIds = new Set(completedAssignments.map(a => a.jobId));

  // Filter out applications for jobs that already have an assignment
  // (prevents showing "Foley" in both Applications and Active Projects)
  const pendingApplications = workerApplications.filter(app => !assignedJobIds.has(app.jobId));

  const applicationByJobId = new Map();
  workerApplications.forEach(app => {
    if (app?.jobId) applicationByJobId.set(app.jobId, app);
  });

  const workerAppliedJobs = dataSource === 'supabase'
    ? new Set([...appliedJobs, ...workerApplications.map(app => app.jobId)])
    : appliedJobs;
  const workerAcceptedJobs = dataSource === 'supabase'
    ? new Set([
        ...acceptedJobs,
        ...workerApplications.filter(app => app.stage === 'accepted').map(app => app.jobId),
        ...onboardingAssignments.map(assignment => assignment.jobId),
      ])
    : acceptedJobs;
  const workerStartedJobs = dataSource === 'supabase'
    ? new Set([...startedJobs, ...activeAssignments.map(assignment => assignment.jobId)])
    : startedJobs;

  const onboardingItems = dataSource === 'supabase'
    ? (() => {
        const items = new Map();
        onboardingAssignments.forEach(assignment => {
          items.set(assignment.jobId, buildAssignmentBackedApplication(assignment, applicationByJobId.get(assignment.jobId)));
        });
        workerApplications.forEach(app => {
          if (app.stage !== 'accepted') return;
          if (activeJobIds.has(app.jobId) || completedJobIds.has(app.jobId)) return;
          if (!items.has(app.jobId)) {
            items.set(app.jobId, app);
          }
        });
        return Array.from(items.values());
      })()
    : applications.filter(app => acceptedJobs.has(app.jobId) && !startedJobs.has(app.jobId));

  // Available jobs = all jobs minus started (Current Jobs) and applied (My Applications)
  const availableJobs = allJobs.filter(j => !workerStartedJobs.has(j.id) && !workerAppliedJobs.has(j.id));

  // Job counts by domain — for domain tabs and summary stats
  const jobCounts = {
    available: {}, // jobs user can browse
    current: {},   // jobs user is working on
  };
  DOMAINS.forEach(d => { jobCounts.available[d] = 0; jobCounts.current[d] = 0; });
  allJobs.forEach(j => {
    const key = workerStartedJobs.has(j.id) ? 'current' : (workerAppliedJobs.has(j.id) ? null : 'available');
    if (key) {
      jobCounts[key][j.domain] = (jobCounts[key][j.domain] || 0) + 1;
      jobCounts[key]['All'] = (jobCounts[key]['All'] || 0) + 1;
    }
  });

  // Derive rate filter options from actual job data
  const allRates = [...new Set(availableJobs.map(j => j.rate))].sort((a, b) => a - b);
  const _minRateOptions = [0, ...allRates.filter((r, i, arr) => i === 0 || r >= arr[0] + 2)];
  const _maxRateOptions = [...allRates.filter((r, i, arr) => i === arr.length - 1 || r <= arr[arr.length - 1] - 2), Infinity];

  const filteredJobs = allJobs
    // Show all jobs including assigned — assigned jobs get "ASSIGNED" badge + "Go to Job" CTA
    .filter(j => !workerAppliedJobs.has(j.id) || justApplied.has(j.id) || workerStartedJobs.has(j.id) || workerAcceptedJobs.has(j.id))
    .filter(j => activeDomain === 'All' || j.domain === activeDomain)
    .filter(j => j.rate >= minRate && j.rate <= maxRate)
    .filter(j => !searchQuery || (j.title || '').toLowerCase().includes(searchQuery.toLowerCase()) || (j.client || '').toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (activeSort === 'Recommended') return b.match - a.match;
      if (activeSort === 'Highest Pay') return b.rate - a.rate;
      if (activeSort === 'Closing Soon') return (a.status === 'closing_soon' ? -1 : (b.status === 'closing_soon' ? 1 : 0));
      if (activeSort === 'Newest') return 0; // Already in posted order
      return 0;
    });

  const selectedJob = selectedJobId ? allJobs.find(j => j.id === selectedJobId) : null;

  const handleQuickApply = (jobId) => {
    if (quickApplyConfirm === jobId) {
      const jobData = allJobs.find(j => j.id === jobId);
      // Instant Match jobs go straight to started
      if (jobData?.instantMatch) {
        startJob(jobId, jobData);
      } else {
        applyToJob(jobId, jobData);
      }
      // Track as just-applied in this session — card stays visible with confirmation
      setJustApplied(prev => new Set([...prev, jobId]));
      setQuickApplyConfirm(null);
      setSelectedJobId(null); // Close split-view
    } else {
      setQuickApplyConfirm(jobId);
      setTimeout(() => setQuickApplyConfirm(null), 5000);
    }
  };

  return (
    <div>
      {/* God Mode hover tooltip */}
      {isGodMode && hoveredEntity && hoveredEntity.rect && (
        <GodModeTooltip type={hoveredEntity.type} entity={hoveredEntity.entity} rect={hoveredEntity.rect} />
      )}
      {/* Header */}
      {isAnnotatorRoute ? (
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 24, fontWeight: 600, color: "var(--text-primary)", margin: '0 0 4px 0', fontFamily: 'var(--font-sans)' }}>
            {routeTitle}
          </h1>
        </div>
      ) : (
        <div className="page-header">
          <div className="section-title">Jobs
          </div>
          <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--text-white)', marginTop: 4 }}>
            Find your next job
          </div>
        </div>
      )}

      {/* Tabs — show full tab bar for legacy route, subtabs for annotator routes with multiple tabs */}
      {isAnnotatorRoute ? (
        routeSubtabs ? (
          <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
            {routeSubtabs.map(tab => {
              const count = tab === 'My Jobs' ? activeAssignments.length + (currentAssignments.completed || []).length
                : tab === 'My Applications' ? pendingApplications.length
                : tab === 'Saved Jobs' ? savedJobCount
                : null;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`filter-pill ${activeTab === tab ? 'active' : ''}`}
                >{tab.replace('My ', '').replace(' Jobs', '')}{count != null ? ` (${count})` : ''}</button>
              );
            })}
          </div>
        ) : null
      ) : (
        <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 20px', fontSize: 12, fontFamily: 'var(--font-mono)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: activeTab === tab ? 'var(--text-white)' : 'var(--text-muted)',
                borderBottom: activeTab === tab ? '2px solid var(--accent-teal)' : '2px solid transparent',
                transition: 'all 0.15s', letterSpacing: '0.5px',
              }}
            >{tab}</button>
          ))}
        </div>
      )}

      {/* Browse Jobs Tab */}
      {activeTab === 'Browse Jobs' && (
        <>
          {_dbLoading && dataSource === 'supabase' && <div style={{ padding: 40, textAlign: 'center', color: "var(--text-muted)", fontSize: 12 }}>Loading jobs...</div>}
          {/* Search bar (matches production) */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                placeholder="Search by title, client, or keyword..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  width: '100%', padding: '8px 12px',
                  background: "var(--bg-card)", border: '1px solid var(--border)',
                  borderRadius: 6, color: "var(--text-primary)",
                  fontSize: 12, outline: 'none',
                  transition: 'border-color 0.15s',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--text-success)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>
          </div>

          {/* Domain tabs + Sort */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              {DOMAINS.slice(0, 5).map(domain => {
                const isActive = activeDomain === domain;
                const count = jobCounts.available[domain] || 0;
                return (
                  <button key={domain} onClick={() => { setActiveDomain(activeDomain === domain && domain !== 'All' ? 'All' : domain); setSelectedJobId(null); }} style={{
                    padding: '4px 10px', borderRadius: 4,
                    fontSize: 12, cursor: 'pointer',
                    border: isActive ? '1px solid var(--text-primary)' : '1px solid var(--border)',
                    background: isActive ? 'var(--text-primary)' : 'transparent',
                    color: isActive ? 'var(--bg)' : 'var(--text-secondary)',
                    transition: 'all 0.15s', whiteSpace: 'nowrap', fontWeight: isActive ? 500 : 400,
                  }}>{domain} {count > 0 ? count : ''}</button>
                );
              })}
              {/* Overflow dropdown for remaining domains */}
              <select
                value={DOMAINS.slice(5).includes(activeDomain) ? activeDomain : ''}
                onChange={e => { if (e.target.value) { setActiveDomain(e.target.value); setSelectedJobId(null); } }}
                style={{
                  padding: '4px 8px', fontSize: 12, border: `1px solid ${DOMAINS.slice(5).includes(activeDomain) ? 'var(--text-primary)' : 'var(--border)'}`,
                  borderRadius: 4, background: DOMAINS.slice(5).includes(activeDomain) ? 'var(--text-primary)' : 'transparent',
                  color: DOMAINS.slice(5).includes(activeDomain) ? 'var(--bg)' : 'var(--text-secondary)',
                  cursor: 'pointer', outline: 'none',
                }}
              >
                <option value="">More ▾</option>
                {DOMAINS.slice(5).map(d => <option key={d} value={d}>{d} {jobCounts.available[d] || 0}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              {/* Rate filter */}
              <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginRight: 2 }}>RATE</span>
              <select value={minRate} onChange={e => setMinRate(Number(e.target.value))} style={{
                padding: '3px 6px', fontSize: 10, fontFamily: 'var(--font-mono)',
                background: 'var(--bg-card)', color: 'var(--text-secondary)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)',
                outline: 'none', cursor: 'pointer',
              }}>
                <option value={0}>$0+</option>
                {allRates.map(r => (
                  <option key={r} value={r}>${r}+</option>
                ))}
              </select>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>–</span>
              <select value={maxRate} onChange={e => setMaxRate(Number(e.target.value))} style={{
                padding: '3px 6px', fontSize: 10, fontFamily: 'var(--font-mono)',
                background: 'var(--bg-card)', color: 'var(--text-secondary)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)',
                outline: 'none', cursor: 'pointer',
              }}>
                <option value={9999}>Any</option>
                {allRates.map(r => (
                  <option key={r} value={r}>${r}</option>
                ))}
              </select>

              <div style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 4px' }} />

              <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginRight: 2 }}>SORT</span>
              {SORTS.map(sort => (
                <button key={sort} onClick={() => setActiveSort(activeSort === sort ? 'Recommended' : sort)} style={{
                  padding: '4px 10px', borderRadius: 'var(--radius-pill)',
                  fontSize: 10, fontFamily: 'var(--font-mono)', cursor: 'pointer',
                  border: activeSort === sort ? '1px solid var(--accent-teal)' : '1px solid var(--border)',
                  background: activeSort === sort ? 'rgba(var(--accent-teal-rgb), 0.06)' : 'transparent',
                  color: activeSort === sort ? 'var(--accent-teal)' : 'var(--text-muted)',
                  transition: 'all 0.15s',
                }}>{sort}</button>
              ))}
            </div>
          </div>

          {/* Stats bar */}
          <div style={{
            display: 'flex', gap: 24, padding: '8px 0', marginBottom: 16,
            fontSize: 11, color: "var(--text-muted)",
          }}>
            <span><span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{filteredJobs.length}</span> {activeDomain === 'All' ? 'total' : activeDomain} jobs</span>
            <span><span style={{ color: "var(--text-success)" }}>{filteredJobs.filter(j => j.instantMatch).length}</span> instant matches</span>
            <span><span style={{ color: "var(--text-error)" }}>{filteredJobs.filter(j => j.status === 'closing_soon').length}</span> closing soon</span>
          </div>

          {/* Split view: Job cards (left) + Context panel (right) — always visible like boss projects */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>

          {/* Left column: Job cards (scrollable, ~5-6 visible) */}
          <div>
          <div style={{ display: 'grid', gridTemplateColumns: selectedJob ? '1fr' : 'repeat(2, 1fr)', gap: 12, maxHeight: 720, overflowY: 'auto', paddingRight: 4 }}>
            {filteredJobs.length === 0 && (
              <div style={{ gridColumn: '1 / -1', padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                No jobs match your filters. Try adjusting your search criteria.
              </div>
            )}
            {filteredJobs.map(job => {
              const isApplied = workerAppliedJobs.has(job.id);
              const isConfirming = quickApplyConfirm === job.id;
              const boostedRate = hasRateBoost ? Math.round(job.rate * 1.1) : null;

              // Just-applied confirmation card
              if (justApplied.has(job.id)) {
                return (
                  <div key={job.id} style={{
                    background: 'rgba(var(--accent-teal-rgb), 0.03)',
                    border: '1px solid rgba(var(--accent-teal-rgb), 0.25)',
                    borderRadius: 'var(--radius-card)', padding: '20px 22px',
                    transition: 'all 0.3s',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-white)' }}>{job.title}</div>
                      <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--accent-teal)', background: 'rgba(var(--accent-teal-rgb), 0.1)', padding: '3px 12px', borderRadius: 'var(--radius-pill)' }}>Applied</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'rgba(var(--accent-teal-rgb), 0.04)', borderRadius: 'var(--radius-stat)', border: '1px solid rgba(var(--accent-teal-rgb), 0.1)' }}>
                      <span style={{ fontSize: 20 }}>✓</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--accent-teal)', marginBottom: 2 }}>Application Submitted</div>
                        <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>You can track your application status in My Applications</div>
                      </div>
                    </div>
                  </div>
                );
              }

              const cardQual = getJobQualification(job);
              const canQuickApply = cardQual.canQuickApply;
              const staffedPct = job.teamSize > 0 ? Math.round(((job.currentStaffed || 0) / job.teamSize) * 100) : 0;
              const isWorking = workerStartedJobs.has(job.id);
              const isOnboarding = workerAcceptedJobs.has(job.id);
              const statusLabel = isWorking ? 'ASSIGNED' : isOnboarding ? 'ONBOARDING' : job.status === 'closing_soon' ? 'CLOSING SOON' : job.instantMatch ? 'INSTANT' : job.status === 'Open' ? 'OPEN' : job.status?.toUpperCase();
              const statusColor = isWorking ? 'var(--text-success)' : isOnboarding ? 'var(--text-warning)' : job.status === 'closing_soon' ? 'var(--text-error)' : 'var(--text-success)';

              return (
                <div key={job.id} onClick={(e) => { if (e.target.closest('button') || e.target.closest('a') || e.target.closest('svg')) return; setSelectedJobId(job.id === selectedJobId ? null : job.id); }} style={{
                  background: "var(--bg-card)",
                  border: `1px solid ${selectedJobId === job.id ? 'var(--text-success)' : 'var(--border)'}`,
                  borderRadius: 8, padding: 14,
                  boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px, rgba(0,0,0,0.06) 0px 1px 2px',
                  transition: 'all 0.15s', cursor: 'pointer',
                }}
                  onMouseEnter={e => { if (selectedJobId !== job.id) e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.08) 0px 2px 8px'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.04) 0px 1px 3px, rgba(0,0,0,0.06) 0px 1px 2px'; }}
                >
                  {/* Row 1: Title + Save + Status badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <Link to={`/job/${job.id}`} onClick={e => e.stopPropagation()} style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>
                      {job.title}
                    </Link>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span onClick={(e) => { e.stopPropagation(); toggleSaveJob(job.id); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'opacity 0.15s' }} title={savedJobs.has(job.id) ? 'Saved' : 'Save'}>
                        <svg width="12" height="14" viewBox="0 0 14 16" fill={savedJobs.has(job.id) ? 'var(--text-success)' : 'none'} stroke={savedJobs.has(job.id) ? 'var(--text-success)' : 'var(--text-muted)'} strokeWidth="1.5"><path d="M1.5 1.5H12.5V14.5L7 11L1.5 14.5V1.5Z"/></svg>
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 600, color: statusColor }}>{statusLabel}</span>
                    </div>
                  </div>
                  {/* Row 2: Domain + client if creator opted to show (is_confidential === false) */}
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>
                    {job.client && job.isConfidential === false ? `${job.client} · ` : ''}{job.domain}{job.duration ? ` · ${job.duration}` : ''}
                  </div>
                  {/* Row 3: Posted + description */}
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
                    {job.briefDescription || job.brief_description || `${job.domain} annotation project`}
                  </div>
                  {/* Row 4: Metrics grid (matching boss project card) */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: staffedPct > 0 ? 8 : 0 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)" }}>{job.currentStaffed || 0}/{job.teamSize || '?'}</div>
                      <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: "var(--text-muted)", textTransform: 'uppercase' }}>Spots</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)" }}>{job.hoursPerWeek || '—'}</div>
                      <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: "var(--text-muted)", textTransform: 'uppercase' }}>Hrs/Wk</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)" }}>${boostedRate || job.rate}/hr</div>
                      <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: "var(--text-muted)", textTransform: 'uppercase' }}>Rate</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {workerStartedJobs.has(job.id) ? (
                        <Link to={`/job/${job.id}`} onClick={e => e.stopPropagation()} style={{ padding: '4px 10px', fontSize: 10, fontFamily: 'var(--font-mono)', background: 'transparent', color: "var(--text-primary)", border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', textDecoration: 'none' }}>Go to Job →</Link>
                      ) : workerAcceptedJobs.has(job.id) ? (
                        <Link to={`/job/${job.id}`} onClick={e => e.stopPropagation()} style={{ padding: '4px 10px', fontSize: 10, fontFamily: 'var(--font-mono)', background: 'transparent', color: "var(--text-primary)", border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', textDecoration: 'none' }}>Onboarding →</Link>
                      ) : isApplied ? (
                        <span style={{ fontSize: 10, fontWeight: 500, color: "var(--text-muted)" }}>Applied ✓</span>
                      ) : isConfirming ? (
                        <button onClick={(e) => { e.stopPropagation(); handleQuickApply(job.id); }} style={{ padding: '4px 10px', fontSize: 10, fontFamily: 'var(--font-mono)', background: "var(--accent-teal)", color: "var(--text-primary)", border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>Confirm</button>
                      ) : canQuickApply ? (
                        <button onClick={(e) => { e.stopPropagation(); handleQuickApply(job.id); }} style={{ padding: '4px 10px', fontSize: 10, fontFamily: 'var(--font-mono)', background: 'transparent', color: "var(--text-primary)", border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer' }}>{job.instantMatch ? 'Start Now' : 'Apply'}</button>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); setSelectedJobId(job.id); }} style={{ padding: '4px 10px', fontSize: 10, fontFamily: 'var(--font-mono)', background: 'transparent', color: "var(--text-muted)", border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer' }}>Apply</button>
                      )}
                    </div>
                  </div>
                  {/* Row 5: Staffing progress bar */}
                  {staffedPct > 0 && staffedPct < 100 && (
                    <div>
                      <div style={{ width: '100%', height: 3, background: "var(--border)", borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ width: `${staffedPct}%`, height: '100%', background: "var(--accent-teal)", borderRadius: 2 }} />
                      </div>
                      <div style={{ fontSize: 9, color: "var(--text-muted)", textAlign: 'right', marginTop: 2 }}>{staffedPct}% staffed</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          </div>
          {/* end left column */}

          {/* Right panel — always visible (320px, matches boss projects Activity panel) */}
          <div style={{ position: 'sticky', top: 0, height: 'fit-content', alignSelf: 'start' }}>
          {selectedJob ? (
            <div style={{
              background: "var(--bg-card)", border: '1px solid var(--border)',
              borderRadius: 8, padding: '18px',
              overflowY: 'auto',
            }}>
              {/* Header: Title + Rate + Close */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>{selectedJob.title}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {selectedJob.client && selectedJob.isConfidential === false ? `${selectedJob.client} · ` : ''}{selectedJob.domain}{selectedJob.duration ? ` · ${selectedJob.duration}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 18, fontFamily: 'var(--font-mono)', fontWeight: 600, color: "var(--text-primary)" }}>
                    ${hasRateBoost ? Math.round(selectedJob.rate * 1.1) : selectedJob.rate}/hr
                  </div>
                  {hasRateBoost && <span style={{ fontSize: 9, color: "var(--text-success)", fontWeight: 500 }}>Boosted</span>}
                  <span onClick={() => setSelectedJobId(null)} style={{
                    cursor: 'pointer', fontSize: 14, color: "var(--text-muted)",
                    width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '50%', transition: 'all 0.15s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--border-light)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
                  >✕</span>
                </div>
              </div>

              {/* Meta grid — no team size (redundant with sourced) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                {[
                  { label: 'Spots', value: `${selectedJob.currentStaffed || 0}/${selectedJob.teamSize}` },
                  { label: 'Start Date', value: selectedJob.startDate || 'Not set' },
                  { label: 'Hrs/Week', value: selectedJob.hoursPerWeek || '—' },
                  ...(selectedJob.referralBonus ? [{ label: 'Referral Bonus', value: `$${selectedJob.referralBonus}` }] : [{ label: 'Posted', value: selectedJob.posted || '—' }]),
                ].map(item => (
                  <div key={item.label} style={{ padding: '8px 10px', background: "var(--bg-stat)", borderRadius: 6 }}>
                    <div style={{ fontSize: 9, fontWeight: 500, color: "var(--text-muted)", textTransform: 'uppercase', marginBottom: 3 }}>{item.label}</div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}>{item.value}</div>
                  </div>
                ))}
              </div>

              {/* Application checklist */}
              {(() => {
                const isStarted = workerStartedJobs.has(selectedJob.id);
                const isAccepted = workerAcceptedJobs.has(selectedJob.id);
                const isApplied = workerAppliedJobs.has(selectedJob.id);
                // Cross-domain aware: check qualifications for THIS job's domain, not just user's primary
                const jobQual = getJobQualification(selectedJob);
                const domainAssessmentDone = jobQual.steps.domainAssessment;
                const skillsEvalDone = jobQual.steps.skillsEvaluation;
                const hasSpecialAssessment = selectedJob.assessment_type === 'specialized';
                const steps = [
                  { name: 'Resume', done: completedSteps.resume, action: 'Upload', nav: () => navigate('/profile'), desc: 'Upload your resume to enable Quick Apply' },
                  { name: `${selectedJob.domain} Domain Assessment`, done: domainAssessmentDone, action: 'Take Assessment', nav: () => navigate('/assessments'), desc: hasSpecialAssessment ? `Specialized ${selectedJob.domain} assessment required` : `Pass the ${selectedJob.domain} domain assessment` },
                  { name: 'Skills Evaluation', done: skillsEvalDone, action: 'Start Evaluation', nav: () => navigate('/assessments'), desc: 'Complete a practical annotation task' },
                  { name: 'Work Authorization', done: completedSteps.workAuth, action: 'Verify', nav: () => navigate('/profile'), desc: 'One-time contractor eligibility verification' },
                ];
                const completedCount = steps.filter(s => s.done).length;
                const allDone = completedCount === 4;

                // Status-aware header
                const headerText = isStarted
                  ? `Active Job · ${completedCount}/4 steps complete`
                  : isAccepted
                    ? `Accepted · ${completedCount}/4 steps complete`
                    : isApplied
                      ? `Applied · ${completedCount}/4 steps complete`
                      : `Application · ${completedCount} of 4 steps`;

                return (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.48px', marginBottom: 8 }}>
                      {headerText}
                    </div>
                    <div style={{ width: '100%', height: 3, background: "var(--border)", borderRadius: 2, marginBottom: 12, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(completedCount / 4) * 100}%`, background: "var(--accent-teal)", borderRadius: 2, transition: 'width 0.3s' }} />
                    </div>
                    {steps.map((step, i) => (
                      <div key={step.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < 3 ? '1px solid var(--border-light)' : 'none' }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                          border: `1.5px solid ${step.done ? 'var(--text-success)' : 'var(--border)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, color: step.done ? 'var(--text-success)' : 'var(--text-muted)',
                        }}>{step.done ? '✓' : i + 1}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: step.done ? 'var(--text-muted)' : 'var(--text-primary)' }}>{step.name}</div>
                          {!step.done && <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 1 }}>{step.desc}</div>}
                        </div>
                        {step.done ? (
                          <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: "var(--text-muted)" }}>Done</span>
                        ) : (
                          <button onClick={step.nav} style={{
                            padding: '3px 10px', fontSize: 9, fontFamily: 'var(--font-mono)',
                            background: 'transparent', color: "var(--text-primary)",
                            border: '1px solid var(--border)', borderRadius: 4,
                            cursor: 'pointer', whiteSpace: 'nowrap',
                          }}>{step.action} →</button>
                        )}
                      </div>
                    ))}
                    {/* Referral code input — shown only when not yet applied */}
                    {!isApplied && !isStarted && (
                      <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(250, 199, 117, 0.03)', border: '1px solid rgba(250, 199, 117, 0.1)', borderRadius: 'var(--radius-stat)' }}>
                        <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--accent-gold)', marginBottom: 4 }}>Have a referral code?</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input
                            type="text"
                            placeholder="Enter code"
                            style={{
                              flex: 1, padding: '4px 8px', fontSize: 10, fontFamily: 'var(--font-mono)',
                              background: 'var(--bg-card)', color: 'var(--text-white)',
                              border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)',
                              outline: 'none',
                            }}
                            onClick={e => e.stopPropagation()}
                          />
                          <button onClick={async e => {
                            e.stopPropagation();
                            const input = e.target.previousSibling;
                            const code = input.value.trim();
                            if (!code) return;
                            const { validateReferralCode } = await import('../services/referralService');
                            const result = await validateReferralCode(code);
                            if (result.valid) {
                              input.style.borderColor = 'rgba(var(--accent-teal-rgb), 0.5)';
                              input.value = `✓ Referred by ${result.referrerName}`;
                              input.disabled = true;
                              e.target.textContent = 'Applied ✓';
                              e.target.style.color = 'var(--accent-teal)';
                              e.target.style.borderColor = 'rgba(var(--accent-teal-rgb), 0.3)';
                            } else {
                              input.style.borderColor = 'var(--border-error)';
                              setTimeout(() => { input.style.borderColor = 'var(--border)'; }, 2000);
                            }
                          }} style={{
                            padding: '4px 10px', fontSize: 9, fontFamily: 'var(--font-mono)',
                            background: 'transparent', color: 'var(--accent-gold)',
                            border: '1px solid rgba(250, 199, 117, 0.3)', borderRadius: 'var(--radius-pill)',
                            cursor: 'pointer',
                          }}>Apply</button>
                        </div>
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 8 }}>
                      {isStarted
                        ? 'You are currently working on this job.'
                        : isAccepted
                          ? 'Accepted — onboarding in progress.'
                          : isApplied
                            ? 'Application under review.'
                            : allDone
                              ? 'All steps complete — ready to apply.'
                              : 'Complete all steps to unlock Quick Apply.'
                      }
                    </div>
                  </div>
                );
              })()}

              {/* Match reason hidden per founder feedback — matchReason kept in data */}

              {/* CTA */}
              {(() => {
                // Cross-domain aware: use per-job qualification check
                const ctaQual = getJobQualification(selectedJob);
                const canApply = ctaQual.canQuickApply;
                const isStarted = workerStartedJobs.has(selectedJob.id);
                const isAccepted = workerAcceptedJobs.has(selectedJob.id);
                const isApplied = workerAppliedJobs.has(selectedJob.id);
                return (
              <div style={{ display: 'flex', gap: 8 }}>
                <Link to={`/job/${selectedJob.id}`} style={{
                  flex: 1, padding: '10px', fontSize: 12, fontWeight: 500, textAlign: 'center',
                  background: 'transparent', color: "var(--text-primary)",
                  border: '1px solid var(--border)', borderRadius: 6,
                  textDecoration: 'none',
                }}>Full Details →</Link>
                {isStarted ? (
                  <Link to={`/job/${selectedJob.id}`} style={{
                    flex: 1, padding: '10px', fontSize: 12, fontWeight: 500, textAlign: 'center',
                    background: "var(--bg)", color: "var(--text-primary)",
                    border: 'none', borderRadius: 6,
                    textDecoration: 'none',
                  }}>Go to Job →</Link>
                ) : isApplied || isAccepted ? (
                  <div style={{ flex: 1, padding: '10px', textAlign: 'center', fontSize: 12, fontWeight: 500, color: "var(--text-muted)", background: "var(--bg-stat)", borderRadius: 6 }}>
                    {isAccepted ? 'Onboarding' : 'Under Review'}
                  </div>
                ) : canApply ? (
                  <button onClick={(e) => { e.stopPropagation(); handleQuickApply(selectedJob.id); }} style={{
                    flex: 1, padding: '10px', fontSize: 12, fontWeight: 500,
                    background: "var(--bg)", color: "var(--text-primary)",
                    border: 'none', borderRadius: 6,
                    cursor: 'pointer',
                  }}>
                    {quickApplyConfirm === selectedJob.id ? 'Confirm' : 'Submit Application'}
                  </button>
                ) : (
                  <button onClick={() => {
                    const firstIncomplete = [
                      !ctaQual.steps.resume && 'professional',
                      !ctaQual.steps.domainAssessment && 'Assessments',
                      !ctaQual.steps.skillsEvaluation && 'Assessments',
                      !ctaQual.steps.workAuth && 'professional',
                    ].find(Boolean);
                    if (firstIncomplete === 'professional') navigate('/profile');
                    else navigate('/assessments');
                  }} style={{
                    flex: 1, padding: '10px', fontSize: 12, fontWeight: 500,
                    background: "var(--bg)", color: "var(--text-primary)",
                    border: 'none', borderRadius: 6,
                    cursor: 'pointer',
                  }}>
                    Complete Steps
                  </button>
                )}
              </div>
                );
              })()}
            </div>
          ) : (
            /* Context panel — shown when no job is selected */
            <ContextPanel
              qualifiedDomains={qualifiedDomains}
              applications={pendingApplications}
              savedJobIds={savedJobs}
              dbSavedJobs={dbSavedJobs}
              navigate={navigate}
            />
          )}
          </div>

          </div>
          {/* end split-view grid */}
        </>
      )}

      {/* Current Jobs Tab */}
      {activeTab === 'My Jobs' && (
        <>
          {_dbLoading && dataSource === 'supabase' && <div style={{ padding: 40, textAlign: 'center', color: "var(--text-muted)", fontSize: 12 }}>Loading assignments...</div>}
          {/* ─── Active Projects (hero cards with embedded batch queue) ─── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.48px' }}>
              Active Projects ({activeAssignments.length})
            </span>
          </div>

          {activeAssignments.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', fontSize: 12, color: "var(--text-muted)", background: "var(--bg-card)", borderRadius: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', marginBottom: 20 }}>
              {onboardingItems.length > 0 ? 'No active jobs yet — onboarding assignments below' : 'No active jobs — browse open positions to get started'}
            </div>
          ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
            {activeAssignments.map(aj => {
            const job = aj?.job || {};
            const jobBatchesForCount = (batchesByJob[aj?.jobId] || []);
            // Prefer batch data (accurate) over stale assignment.annotations_completed
            const batchCompleted = jobBatchesForCount.reduce((s, b) => s + (b.items_completed || 0), 0);
            const completed = batchCompleted > 0 ? batchCompleted : (aj?.annotationsCompleted || 0);
            const total = jobBatchesForCount.reduce((s, b) => s + (b.total_items || 0), 0);
            const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
            const quality = aj?.quality ?? 0;
            const hoursLogged = aj?.hoursLogged || 0;
            // R11 MED-4 audit fix: `aj.rate` is canonical (post-R11 jobAdapter).
            const rate = aj?.rate ?? 0;
            const jobId = aj?.jobId || null;
            const computed = hoursLogged * rate;
            const earnings = Math.max(aj?.totalEarned || 0, computed);

            return (
              <div key={aj.id || jobId} style={{ background: "var(--bg-card)", borderRadius: 8, padding: '16px 18px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', transition: 'box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.08) 0px 2px 8px'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.04) 0px 1px 3px'; }}
              >
                {/* Title + badge */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Link to={`/job/${jobId}`} style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 8, transition: 'color 0.15s' }}
                    onMouseEnter={e => { e.target.style.color = 'var(--text-success)'; }}
                    onMouseLeave={e => { e.target.style.color = 'var(--text-primary)'; }}
                  >{job.title || 'Unknown'}</Link>
                  <span style={{ fontSize: 10, fontWeight: 500, color: "var(--text-success)", flexShrink: 0 }}>Active</span>
                </div>
                {/* Domain + rate */}
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>{job.domain} · ${rate}/hr</div>
                {/* KPI row — Hours, Annotations, Quality, Earned */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)", lineHeight: 1 }}>{hoursLogged > 0 ? `${hoursLogged.toFixed(0)}h` : '—'}</div>
                    <div style={{ fontSize: 8, color: "var(--text-muted)", textTransform: 'uppercase', marginTop: 3 }}>Hours</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)", lineHeight: 1 }}>{completed > 0 ? completed : '—'}</div>
                    <div style={{ fontSize: 8, color: "var(--text-muted)", textTransform: 'uppercase', marginTop: 3 }}>Ann.</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-mono)', color: quality >= 90 ? 'var(--text-success)' : quality >= 80 ? 'var(--text-warning)' : quality > 0 ? 'var(--text-error)' : 'var(--text-primary)', lineHeight: 1 }}>{quality > 0 ? `${quality.toFixed(0)}%` : '—'}</div>
                    <div style={{ fontSize: 8, color: "var(--text-muted)", textTransform: 'uppercase', marginTop: 3 }}>Quality</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)", lineHeight: 1 }}>{earnings > 0 ? (earnings >= 1000 ? `$${(earnings / 1000).toFixed(1)}K` : `$${Math.round(earnings)}`) : '—'}</div>
                    <div style={{ fontSize: 8, color: "var(--text-muted)", textTransform: 'uppercase', marginTop: 3 }}>Earned</div>
                  </div>
                </div>
                {/* Progress bar */}
                {total > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 9, color: "var(--text-muted)" }}>{completed}/{total} items</span>
                      <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: "var(--text-success)" }}>{percent}%</span>
                    </div>
                    <div style={{ width: '100%', height: 4, background: "var(--border)", borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${percent}%`, height: '100%', background: "var(--accent-teal)", borderRadius: 2, transition: 'width 0.3s' }} />
                    </div>
                  </div>
                )}
                {/* Pace indicator — items/day vs needed for deadline */}
                {total > 0 && completed > 0 && (() => {
                  const firstBatchStart = jobBatchesForCount
                    .filter(b => b.started_at)
                    .map(b => new Date(b.started_at).getTime())
                    .sort((a, b) => a - b)[0];
                  if (!firstBatchStart) return null;
                  const daysActive = Math.max(1, (Date.now() - firstBatchStart) / 86400000);
                  const currentPace = completed / daysActive;
                  const remaining = total - completed;
                  const deadline = job.endDate ? new Date(job.endDate) : null;
                  const daysLeft = deadline ? Math.max(0, (deadline - Date.now()) / 86400000) : null;
                  const neededPace = daysLeft && daysLeft > 0 ? remaining / daysLeft : null;
                  const isAhead = neededPace != null && currentPace >= neededPace;
                  return (
                    <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: "var(--text-muted)", marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span>{Math.round(currentPace)}/day</span>
                      {neededPace != null && (
                        <>
                          <span>·</span>
                          <span style={{ color: isAhead ? 'var(--text-success)' : 'var(--text-error)' }}>
                            {isAhead ? '✓ on pace' : `need ${Math.round(neededPace)}/day`}
                          </span>
                        </>
                      )}
                      {daysLeft != null && <><span>·</span><span>{Math.round(daysLeft)}d left</span></>}
                    </div>
                  );
                })()}
                {/* Work status — what the annotator actually needs to know.
                    Under 4-state (migration 201), rejection bounces land back in
                    In Progress with rejection_note set. Split so the annotator
                    sees "fresh work" vs "needs revision" vs "awaiting review"
                    as three distinct signals. */}
                {(() => {
                  const inProgress = jobBatchesForCount.filter(b => b.status === BATCH_STATES.IN_PROGRESS);
                  const revisionBatches = inProgress.filter(b => !!b.rejection_note);
                  const activeBatches   = inProgress.filter(b => !b.rejection_note);
                  const submittedBatches = jobBatchesForCount.filter(b => b.status === BATCH_STATES.IN_REVIEW);
                  return (
                    <div style={{ marginBottom: 12 }}>
                      {revisionBatches.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'var(--bg-error)', borderRadius: 6, marginBottom: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 500, color: "var(--text-error)" }}>⚠ {revisionBatches.length} batch{revisionBatches.length > 1 ? 'es' : ''} need revision</span>
                        </div>
                      )}
                      {activeBatches.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(var(--accent-teal-rgb),0.04)', borderRadius: 6, marginBottom: 6 }}>
                          <span style={{ fontSize: 11, color: "var(--text-primary)" }}>{activeBatches.length} batch{activeBatches.length > 1 ? 'es' : ''} ready to work</span>
                        </div>
                      )}
                      {submittedBatches.length > 0 && (
                        <div style={{ fontSize: 10, color: "var(--text-muted)", padding: '0 10px' }}>{submittedBatches.length} submitted · awaiting review</div>
                      )}
                      {activeBatches.length === 0 && revisionBatches.length === 0 && submittedBatches.length === 0 && (
                        <div style={{ fontSize: 11, color: "var(--text-muted)", padding: '6px 10px' }}>No batches assigned yet</div>
                      )}
                    </div>
                  );
                })()}
                {/* Deadline if available */}
                {job.endDate && (
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 12 }}>Deadline: {job.endDate}</div>
                )}
                {/* Log Hours */}
                {(() => {
                  const aId = aj.id;
                  const todayEntry = todayEntries[aId];
                  const weekHrs = (weekEntries[aId] || []).reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
                  const status = logStatus[aId];
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '8px 10px', background: "var(--bg-stat)", borderRadius: 6 }}>
                      <span style={{ fontSize: 10, color: "var(--text-primary)", fontWeight: 500, flexShrink: 0 }}>Log today</span>
                      <input type="number" step="0.5" min="0" max="24" placeholder="0"
                        value={hourInputs[aId] || ''}
                        onChange={e => setHourInputs(prev => ({ ...prev, [aId]: e.target.value }))}
                        style={{ width: 52, padding: '5px 6px', fontSize: 13, fontFamily: 'var(--font-mono)', border: '1px solid var(--border)', borderRadius: 4, textAlign: 'center', outline: 'none', color: "var(--text-primary)" }}
                        onFocus={e => { e.target.style.borderColor = 'var(--text-success)'; }}
                        onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
                      />
                      <span style={{ fontSize: 10, color: "var(--text-muted)" }}>h</span>
                      <button disabled={status === 'saving' || !(parseFloat(hourInputs[aId]) > 0)}
                        onClick={async () => {
                          const hours = parseFloat(hourInputs[aId]);
                          if (!hours || hours <= 0) return;
                          setLogStatus(prev => ({ ...prev, [aId]: 'saving' }));
                          const { error } = await logTimeEntry({ assignmentId: aId, jobId, orgId: currentUser?.org_id || null, hours });
                          if (error) {
                            setLogStatus(prev => ({ ...prev, [aId]: 'error' }));
                            setTimeout(() => setLogStatus(prev => ({ ...prev, [aId]: null })), 2000);
                          } else {
                            setLogStatus(prev => ({ ...prev, [aId]: 'saved' }));
                            const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
                            setTodayEntries(prev => ({ ...prev, [aId]: { hours, date: todayStr } }));
                            setWeekEntries(prev => {
                              const existing = (prev[aId] || []).filter(e => e.date !== todayStr);
                              return { ...prev, [aId]: [...existing, { date: todayStr, hours, status: 'approved', assignment_id: aId }] };
                            });
                            setTimeout(() => setLogStatus(prev => ({ ...prev, [aId]: null })), 2000);
                          }
                        }}
                        style={{
                          padding: '4px 12px', fontSize: 10, fontWeight: 500, borderRadius: 4, cursor: 'pointer', border: 'none',
                          background: status === 'saved' ? 'var(--text-success)' : status === 'error' ? 'var(--text-error)' : todayEntry ? 'var(--border)' : 'var(--text-success)',
                          color: status === 'saved' || (!todayEntry && status !== 'error') ? 'var(--bg-card)' : status === 'error' ? 'var(--bg-card)' : 'var(--text-primary)',
                          opacity: status === 'saving' || !(parseFloat(hourInputs[aId]) > 0) ? 0.5 : 1,
                        }}
                      >{status === 'saving' ? '...' : status === 'saved' ? '✓' : status === 'error' ? '!' : todayEntry ? 'Update' : 'Log'}</button>
                      <span style={{ fontSize: 9, color: "var(--text-muted)", marginLeft: 'auto' }}>{weekHrs > 0 ? `${weekHrs.toFixed(1)}h this week` : ''}</span>
                    </div>
                  );
                })()}
                {/* CTAs */}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => window.open('https://platform.pipelines.tech', '_blank')} style={{
                    flex: 1, padding: '8px', fontSize: 11, fontWeight: 500,
                    background: "var(--accent-teal)", color: "var(--text-primary)", border: 'none', borderRadius: 6, cursor: 'pointer',
                  }}>Start Working →</button>
                  {(() => {
                    const splEmail = aj?.spl?.email;
                    const splName = aj?.spl?.name?.split(' ')[0] || 'there';
                    const href = splEmail
                      ? `mailto:${splEmail}?subject=Question about ${job.title}&body=Hi ${splName},%0A%0A`
                      : `mailto:support@juuniai.com?subject=Question about ${job.title}`;
                    const label = splEmail ? `Contact ${splName}` : 'Contact PM';
                    return (
                      <button onClick={() => window.open(href, '_blank')} disabled={!splEmail}
                        title={splEmail || 'No PM assigned'}
                        style={{
                          padding: '8px 12px', fontSize: 11, fontWeight: 500,
                          background: 'transparent', color: splEmail ? 'var(--text-primary)' : 'var(--text-muted)',
                          border: '1px solid var(--border)', borderRadius: 6,
                          cursor: splEmail ? 'pointer' : 'not-allowed',
                        }}>{label}</button>
                    );
                  })()}
                </div>
              </div>
            );
          })}
          </div>
          )}

          {/* ─── Onboarding (restyled) ─── */}
          {onboardingItems.length > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, marginTop: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.48px' }}>
                  Onboarding ({onboardingItems.length})
                </span>
              </div>
              {onboardingItems.map(app => {
                const ob = onboardingChecklists[app.jobId] || { assignmentStatus: 'Onboarding', pay_confirmed: false };
                const { steps, completed: completedCount, total, allRequiredDone } = getOnboardingState(ob, ob?.job);
                const payConfirmed = ob.pay_confirmed || app.payConfirmed || false;
                const statusText = allRequiredDone && payConfirmed ? 'Awaiting Activation' : allRequiredDone ? 'Awaiting Pay Confirmation' : 'Onboarding';
                return (
                  <div key={app.jobId} style={{ background: "var(--bg-card)", borderRadius: 8, padding: '16px 20px', marginBottom: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{app.jobTitle}</div>
                        {/* R11 MED-4 audit fix: `app.rate` is canonical post-R11
                            (buildAssignmentBackedApplication now writes canonical). */}
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{app.domain} · ${app.rate || 0}/hr</div>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 500, color: "var(--text-warning)" }}>{statusText} ({completedCount}/{total})</span>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {steps.map(s => {
                        const val = getOnboardingStepStatus(ob, s);
                        return (
                          <span key={s.id || s.key} style={{
                            fontSize: 9, padding: '3px 8px', borderRadius: 4,
                            background: val === 'Completed' ? 'rgba(93,202,165,0.08)' : 'var(--bg-stat)',
                            color: val === 'Completed' ? 'var(--text-success)' : val === 'In Progress' ? 'var(--text-warning)' : 'var(--text-muted)',
                            border: `1px solid ${val === 'Completed' ? 'rgba(93,202,165,0.15)' : 'var(--border)'}`,
                          }}>{val === 'Completed' ? '✓' : val === 'In Progress' ? '◐' : '○'} {s.label}</span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* ─── Past Projects (all completed, scrollable) ─── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, marginTop: 20 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.48px' }}>
              Past Projects ({completedAssignments.length})
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20, maxHeight: 400, overflowY: 'auto' }}>
            {completedAssignments.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', padding: '24px', textAlign: 'center', fontSize: 12, color: "var(--text-muted)" }}>No completed projects yet</div>
            ) : completedAssignments.map(a => {
              // R11 BLOCKER-2 audit fix: `a.rate` is now canonical (post-R11 jobAdapter
              // fix). Drop the legacy `confirmedRate || rate` which overrode R4 LOW-5.
              const computed = (a.hoursLogged || 0) * (a.rate || 0);
              const earnings = Math.max(a.totalEarned || 0, computed);
              const job = a.job || {};
              return (
                <div key={a.id || a.jobId} style={{
                  background: "var(--bg-card)", borderRadius: 8, padding: '16px 18px',
                  boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', transition: 'box-shadow 0.15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.08) 0px 2px 8px'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.04) 0px 1px 3px'; }}
                >
                  {/* Title + badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <Link to={`/job/${a.jobId}`} style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 8, transition: 'color 0.15s' }}
                    onMouseEnter={e => { e.target.style.color = 'var(--text-success)'; }}
                    onMouseLeave={e => { e.target.style.color = 'var(--text-primary)'; }}
                  >{job.title || 'Unknown'}</Link>
                    <span style={{ fontSize: 10, fontWeight: 500, color: "var(--text-muted)", flexShrink: 0 }}>Completed</span>
                  </div>
                  {/* Domain + date */}
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
                    {job.domain} · {a.completedAt ? new Date(a.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                  </div>
                  {/* Mini KPIs */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, marginBottom: 10 }}>
                    {[
                      { value: `${(a.hoursLogged || 0).toFixed(0)}h`, label: 'Hours' },
                      { value: `${a.quality || 0}%`, label: 'Quality' },
                      { value: `$${earnings > 1000 ? (earnings / 1000).toFixed(1) + 'K' : earnings.toFixed(0)}`, label: 'Earned' },
                    ].map((kpi, i, arr) => (
                      <div key={kpi.label} style={{ textAlign: 'center', padding: '6px 0', borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)" }}>{kpi.value}</div>
                        <div style={{ fontSize: 8, fontWeight: 500, color: "var(--text-muted)", textTransform: 'uppercase', letterSpacing: '0.3px', marginTop: 2 }}>{kpi.label}</div>
                      </div>
                    ))}
                  </div>
                  {/* CTA */}
                  <Link to={`/job/${a.jobId}`} style={{
                    display: 'block', padding: '8px', fontSize: 11, fontWeight: 500, textAlign: 'center',
                    color: "var(--text-primary)", border: '1px solid var(--border)', borderRadius: 6, textDecoration: 'none',
                  }}>View Details →</Link>
                </div>
              );
            })}
          </div>

          {/* ─── Lifetime Stats ─── */}
          <div style={{ padding: '10px 16px', background: "var(--bg-stat)", border: '1px solid var(--border)', borderRadius: 8, fontSize: 11, color: "var(--text-primary)" }}>
            {(() => {
              // Compute from actual assignment data (not stale individual.total_hours_worked)
              const allAssigns = [...activeAssignments, ...onboardingAssignments, ...completedAssignments];
              const lifetimeHours = allAssigns.reduce((s, a) => s + (a.hoursLogged || 0), 0);
              const lifetimeEarned = allAssigns.reduce((s, a) => s + (a.totalEarned || (a.hoursLogged || 0) * (a.rate || 0)), 0);
              const completedJobCount = new Set(completedAssignments.map(a => a.jobId).filter(Boolean)).size;
              return <><span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px' }}>Work Summary</span> — {completedJobCount} completed · {lifetimeHours.toFixed(0)}h logged · ${lifetimeEarned.toLocaleString('en-US', { maximumFractionDigits: 0 })} from work</>;
            })()}
          </div>
        </>
      )}

      {/* My Applications Tab */}
      {activeTab === 'My Applications' && (
        <>
          {/* Pipeline KPI boxes — single row, clickable to filter */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0, marginBottom: 24 }}>
            {[
              { stage: null, label: 'All', count: pendingApplications.length },
              { stage: 'under_review', label: 'Under Review', count: pendingApplications.filter(a => a.stage === 'under_review' || a.stage === 'applied' || a.stage === 'Applied').length },
              { stage: 'assessment', label: 'Trial Task', count: pendingApplications.filter(a => a.stage === 'assessment' || a.stage === 'skills_evaluation' || a.stage === 'Skills Evaluation').length },
              { stage: 'accepted', label: 'Accepted', count: pendingApplications.filter(a => a.stage === 'accepted').length },
              { stage: 'not_selected', label: 'Not Selected', count: pendingApplications.filter(a => a.stage === 'not_selected').length },
            ].map((s, i, arr) => (
              <div key={s.stage || 'all'} onClick={() => setSelectedJobId(selectedJobId === `filter:${s.stage}` ? null : `filter:${s.stage}`)}
                style={{
                  textAlign: 'center', padding: '12px 8px', cursor: 'pointer',
                  borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                  background: selectedJobId === `filter:${s.stage}` ? 'var(--border-light)' : 'transparent',
                }}>
                <div style={{ fontSize: 28, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)", lineHeight: 1 }}>{s.count}</div>
                <div style={{ fontSize: 9, fontWeight: 500, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: 6 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Application cards — two column grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            {(() => {
              const filterStage = typeof selectedJobId === 'string' && selectedJobId.startsWith('filter:') ? selectedJobId.replace('filter:', '') : null;
              const filtered = filterStage === 'null' || !filterStage ? pendingApplications : pendingApplications.filter(a => {
                if (filterStage === 'under_review') return a.stage === 'under_review' || a.stage === 'applied' || a.stage === 'Applied';
                if (filterStage === 'assessment') return a.stage === 'assessment' || a.stage === 'skills_evaluation' || a.stage === 'Skills Evaluation';
                return a.stage === filterStage;
              });
              return filtered.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', fontSize: 12, color: "var(--text-muted)" }}>No applications{filterStage ? ' in this stage' : ' yet'}</div>
              ) : filtered.map(app => {
              const config = stageConfig[app.stage] || { label: app.stage, color: "var(--text-muted)" };
              const stageColor = app.stage === 'accepted' ? 'var(--text-success)' : app.stage === 'not_selected' ? 'var(--text-error)' : 'var(--text-muted)';
              return (
                <div key={app.id} onClick={() => app.jobId && navigate(`/job/${app.jobId}`)}
                  style={{
                  background: "var(--bg-card)", border: '1px solid var(--border)',
                  borderRadius: 8, padding: '12px 16px', cursor: 'pointer',
                  boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', transition: 'box-shadow 0.15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.08) 0px 2px 8px'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.04) 0px 1px 3px'; }}
                >
                  {/* Row 1: Title + Stage */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{app.jobTitle}</span>
                    <span style={{ fontSize: 10, fontWeight: 500, fontFamily: 'var(--font-mono)', color: stageColor }}>{config.label}</span>
                  </div>
                  {/* Row 2: Domain + Applied date */}
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
                    {app.domain}{app.appliedDate ? ` · Applied ${app.appliedDate}` : ''}
                  </div>
                  {/* Row 3: Rate + spots */}
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, fontFamily: 'var(--font-mono)', color: "var(--text-primary)" }}>
                    <span>${app.rate}/hr</span>
                    <span>{app.currentStaffed || 0}/{app.teamSize || '?'} spots</span>
                  </div>
                </div>
              );
              });
            })()}
          </div>
        </>
      )}

      {/* Saved Jobs Tab */}
      {activeTab === 'Saved Jobs' && (
        <SavedJobsTab
          savedJobs={savedJobs}
          dbSavedJobs={dbSavedJobs}
          allJobs={allJobs}
          toggleSaveJob={toggleSaveJob}
          navigate={navigate}
          assignedJobIds={assignedJobIds}
        />

      )}

      {/* Assessments Tab */}
      {activeTab === 'Assessments' && (() => {
        // Wait for real data before rendering — prevents flash of fallback content
        if (_dbLoading && dataSource === 'supabase') return <div style={{ padding: 40, textAlign: 'center', color: "var(--text-muted)", fontSize: 12 }}>Loading assessments...</div>;
        const passed = assessments.filter(a => a.status === 'passed' && a.type !== 'compliance');
        const available = assessments.filter(a => a.status === 'available' && a.type !== 'compliance');
        const passedDomains = new Set(passed.filter(a => a.domain).map(a => a.domain));
        // Project onboarding: onboarding-status assignments (accepted but not yet active)
        // + any pending applications in assessment/skills_evaluation stage
        const trialTasks = [
          ...onboardingAssignments.map(a => ({ id: a.id, jobId: a.jobId, jobTitle: a.job?.title || 'Job', domain: a.job?.domain || '', source: 'onboarding' })),
          ...pendingApplications.filter(a => a.stage === 'assessment' || a.stage === 'skills_evaluation' || a.stage === 'Skills Evaluation')
            .map(a => ({ id: a.id, jobId: a.jobId, jobTitle: a.jobTitle || 'Job', domain: a.domain || '', source: 'application' })),
        ];
        // All unique domains from jobs + assessments
        const allDomains = [...new Set([...allJobs.map(j => j.domain), ...assessments.map(a => a.domain)].filter(Boolean))].sort();
        // Jobs per domain for "unlocks X jobs"
        const jobsByDomain = {};
        for (const j of allJobs) { if (j.domain) jobsByDomain[j.domain] = (jobsByDomain[j.domain] || 0) + 1; }

        return (
        <>
          {/* ─── Domain Skill Map ─── */}
          <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>Your Domains</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {allDomains.map(domain => {
                const isQualified = passedDomains.has(domain);
                const domainPassed = passed.filter(a => a.domain === domain);
                const expiringSoon = domainPassed.some(a => a.expiresAt && (new Date(a.expiresAt) - new Date()) / 86400000 < 30);
                return (
                  <div key={domain} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                    background: isQualified ? 'rgba(93,202,165,0.08)' : 'var(--bg-stat)',
                    border: `1px solid ${isQualified ? 'rgba(93,202,165,0.25)' : 'var(--border)'}`,
                    borderRadius: 6,
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: isQualified ? (expiringSoon ? 'var(--text-warning)' : 'var(--text-success)') : 'var(--border)', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 500, color: isQualified ? 'var(--text-primary)' : 'var(--text-muted)' }}>{domain}</span>
                    <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: isQualified ? 'var(--text-success)' : 'var(--text-muted)' }}>
                      {isQualified ? (expiringSoon ? 'Expiring' : 'Qualified') : `${jobsByDomain[domain] || 0} jobs`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ─── Tier 2: Project Onboarding (post-acceptance, required before starting) ─── */}
          {trialTasks.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Project Onboarding ({trialTasks.length})</div>
              <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 10 }}>Complete these before you can start working on accepted projects.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {trialTasks.map(task => (
                  <div key={task.id} onClick={() => task.jobId && navigate(`/job/${task.jobId}`)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: "var(--bg-card)", borderRadius: 8, padding: '12px 16px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', border: '1px solid rgba(245,158,11,0.2)', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.querySelector('[data-title]').style.color = 'var(--text-success)'; }}
                    onMouseLeave={e => { e.currentTarget.querySelector('[data-title]').style.color = 'var(--text-primary)'; }}>
                    <div>
                      <div data-title style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", transition: 'color 0.15s' }}>{task.jobTitle}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                        {task.domain}{task.source === 'onboarding' ? ' · Onboarding in progress' : ' · Assessment required'}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 500, padding: '5px 14px', background: "var(--bg)", color: "var(--text-primary)", borderRadius: 4, flexShrink: 0 }}>View</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── Passed Credentials with linked jobs ─── */}
          {passed.length > 0 && (() => {
            const activeWork = [...(currentAssignments.active || []), ...(currentAssignments.onboarding || [])];
            return (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>Your Qualifications ({passed.length})</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {passed.map(assess => {
                  const expiringSoon = assess.expiresAt && (new Date(assess.expiresAt) - new Date()) / 86400000 < 30;
                  const domainJobs = assess.domain ? activeWork.filter(a => {
                    const jobDomain = (a.job?.domain || '').toLowerCase();
                    return jobDomain === assess.domain.toLowerCase();
                  }) : [];
                  const availableJobs = assess.domain ? allJobs.filter(j => j.domain === assess.domain && j.status !== 'Completed').length : 0;
                  return (
                    <div key={assess.id} style={{ background: "var(--bg-card)", borderRadius: 8, padding: '12px 16px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{assess.name}</span>
                          <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: expiringSoon ? 'rgba(245,158,11,0.1)' : 'rgba(93,202,165,0.1)', color: expiringSoon ? 'var(--text-warning)' : 'var(--text-success)' }}>{expiringSoon ? 'Expiring' : 'Passed'}</span>
                        </div>
                        <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: "var(--text-muted)" }}>{assess.dateTaken || ''}</span>
                      </div>
                      {domainJobs.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                          {domainJobs.map(a => (
                            <span key={a.id} onClick={() => a.job?.id && navigate(`/job/${a.job.id}`)}
                              style={{ fontSize: 10, padding: '3px 8px', background: 'rgba(93,202,165,0.08)', border: '1px solid rgba(93,202,165,0.2)', borderRadius: 4, color: "var(--text-primary)", cursor: 'pointer', transition: 'color 0.15s' }}
                              onMouseEnter={e => { e.target.style.color = 'var(--text-success)'; }}
                              onMouseLeave={e => { e.target.style.color = 'var(--text-primary)'; }}
                            >{a.job?.title || 'Job'} · {a.status}</span>
                          ))}
                          {availableJobs > domainJobs.length && <span style={{ fontSize: 10, color: "var(--text-muted)", padding: '3px 0' }}>+ {availableJobs - domainJobs.length} more {assess.domain} jobs</span>}
                        </div>
                      ) : (
                        <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>{availableJobs > 0 ? `${availableJobs} ${assess.domain} jobs available` : `${assess.domain} domain qualified`}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })()}

          {/* ─── Tier 1: Domain Qualifications (pre-apply, unlocks ability to apply) ─── */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px' }}>Domain Qualifications ({available.length})</span>
              {available.length > 3 && (
                <input type="text" placeholder="Search..." value={preApplySearch} onChange={e => setPreApplySearch(e.target.value)}
                  style={{ width: 180, padding: '5px 10px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 4, background: "var(--bg-card)", color: "var(--text-primary)", outline: 'none' }} />
              )}
            </div>
            <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 10 }}>Pass domain assessments to unlock Quick Apply for jobs. Valid 6-12 months.</div>
            {(() => {
              const filtered = available.filter(a => !preApplySearch || a.name?.toLowerCase().includes(preApplySearch.toLowerCase()) || a.domain?.toLowerCase().includes(preApplySearch.toLowerCase()));
              return filtered.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: "var(--text-muted)", background: "var(--bg-card)", borderRadius: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
                  {available.length === 0 ? 'All domain assessments completed.' : 'No matches.'}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                  {filtered.map(assess => (
                    <div key={assess.id} style={{ background: "var(--bg-card)", borderRadius: 8, padding: '14px 16px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", marginBottom: 3 }}>{assess.name}</div>
                        <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 6 }}>
                          {assess.domain}{assess.duration ? ` · ${assess.duration}` : ''}
                        </div>
                        {assess.description && <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5, marginBottom: 10 }}>{assess.description}</div>}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Unlocks {jobsByDomain[assess.domain] || assess.unlocks || 0} {assess.domain || ''} jobs</span>
                        <button onClick={() => navigate('/find-work')} style={{ fontSize: 10, fontWeight: 500, padding: '4px 12px', background: "var(--accent-teal)", color: "var(--text-primary)", border: 'none', borderRadius: 4, cursor: 'pointer' }}>Start</button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </>
        );
      })()}

      {/* Earnings Tab */}
      {activeTab === 'Payments' && (() => {
        if (_dbLoading && dataSource === 'supabase') return <div style={{ padding: 40, textAlign: 'center', color: "var(--text-muted)", fontSize: 12 }}>Loading earnings...</div>;

        // ── Compute from real assignment data (not stale transaction/individual fields) ──
        const allAssigns = [...activeAssignments, ...onboardingAssignments, ...completedAssignments];
        const totalHoursAll = allAssigns.reduce((s, a) => s + (a.hoursLogged || 0), 0);
        const totalEarnedAll = allAssigns.reduce((s, a) => {
          // R11 BLOCKER-2 audit fix: `a.rate` is canonical (post-R11 jobAdapter
          // fix). Legacy `confirmedRate || rate` would invert R4 LOW-5 semantic.
          const rate = a.rate || 0;
          const computed = (a.hoursLogged || 0) * rate;
          return s + Math.max(a.totalEarned || 0, computed);
        }, 0);
        const avgRate = totalHoursAll > 0 ? Math.round(totalEarnedAll / totalHoursAll) : 0;

        // Period earnings from time_entry data × assignment rates
        const rateByAssignment = {};
        for (const a of allAssigns) { rateByAssignment[a.id] = a.rate || 0; }
        const allPeriodEntries = periodEntries || Object.values(weekEntries).flat();
        const periodEarnings = allPeriodEntries.reduce((s, e) => {
          const rate = rateByAssignment[e.assignment_id] || allAssigns.find(a => a.job?.id === e.job_id)?.rate || 0;
          return s + parseFloat(e.hours || 0) * rate;
        }, 0);
        const periodHours = allPeriodEntries.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
        // Derived labels/days from the shared preset (this_week / this_month / etc.)
        const periodDays = earningsPreset === 'this_week' ? 7
          : (earningsPreset === 'this_month' || earningsPreset === 'last_month') ? 30
          : 14;
        const presetLabel = {
          all_time: 'All Time',
          this_week: 'This Week',
          this_month: 'This Month',
          last_month: 'Last Month',
          custom: 'Custom',
        }[earningsPreset] || 'Period';

        // Real transactions (if any exist from payment system)
        const realTransactions = transactions.filter(tx => tx.id && !tx.id.startsWith('tx-'));
        const hasRealPayments = realTransactions.length > 0;

        return (
        <>
          {/* ─── Timeframe filter (drives the period KPI + Activity chart below) ─── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Earnings
            </div>
            <TimeframeSelector
              preset={earningsPreset}
              onPresetChange={setEarningsPreset}
              customStart={earningsCustomStart}
              customEnd={earningsCustomEnd}
              onCustomStartChange={setEarningsCustomStart}
              onCustomEndChange={setEarningsCustomEnd}
            />
          </div>

          {/* ─── KPI Row ─── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0, marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border)' }}>
            {[
              { value: totalEarnedAll >= 1000 ? `$${(totalEarnedAll / 1000).toFixed(1)}K` : `$${Math.round(totalEarnedAll)}`, label: 'Total Earned', sub: `${allAssigns.length} assignment${allAssigns.length !== 1 ? 's' : ''}` },
              { value: periodEarnings > 0 ? `$${Math.round(periodEarnings)}` : '—', label: presetLabel, sub: periodHours > 0 ? `${periodHours.toFixed(1)}h logged` : 'no hours' },
              { value: `${totalHoursAll.toFixed(0)}h`, label: 'Total Hours', sub: `${activeAssignments.length} active` },
              // R11 MED-4 audit fix: display uses canonical `rate` (post-R11 jobAdapter).
              { value: avgRate > 0 ? `$${avgRate}/hr` : '—', label: 'Avg Rate', sub: activeAssignments[0]?.rate ? `$${activeAssignments[0].rate} current` : undefined },
              { value: completedAssignments.length, label: 'Completed', sub: completedAssignments.length > 0 ? `${completedAssignments.reduce((s, a) => s + (a.hoursLogged || 0), 0).toFixed(0)}h total` : 'none yet' },
            ].map((kpi, i, arr) => (
              <div key={kpi.label} style={{ textAlign: 'center', padding: '12px 8px', borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontSize: 28, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)", lineHeight: 1 }}>{kpi.value}</div>
                <div style={{ fontSize: 9, fontWeight: 500, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: 6 }}>{kpi.label}</div>
                {kpi.sub && <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>{kpi.sub}</div>}
              </div>
            ))}
          </div>

          {/* ─── Earnings by Project ─── */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>
              Earnings by Project ({allAssigns.length})
            </div>
            {allAssigns.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: "var(--text-muted)", background: "var(--bg-card)", borderRadius: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>No assignments yet — earnings will appear as you work.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {allAssigns.sort((a, b) => (b.hoursLogged || 0) - (a.hoursLogged || 0)).map(a => {
                  // R11 BLOCKER-2 audit fix: `a.rate` is canonical (post-R11
                  // jobAdapter fix). Drop legacy `confirmedRate || rate`.
                  const rate = a.rate || 0;
                  const earned = Math.max(a.totalEarned || 0, (a.hoursLogged || 0) * rate);
                  const periodHrsForThis = allPeriodEntries.filter(e => e.assignment_id === a.id).reduce((s, e) => s + parseFloat(e.hours || 0), 0);
                  return (
                    <div key={a.id} onClick={() => a.job?.id && navigate(`/job/${a.job.id}`)}
                      style={{ background: "var(--bg-card)", borderRadius: 8, padding: '12px 16px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.querySelector('[data-title]').style.color = 'var(--text-success)'; }}
                      onMouseLeave={e => { e.currentTarget.querySelector('[data-title]').style.color = 'var(--text-primary)'; }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <div data-title style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", transition: 'color 0.15s' }}>{a.job?.title || 'Unknown'}</div>
                        <span style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)" }}>${earned >= 1000 ? `${(earned / 1000).toFixed(1)}K` : Math.round(earned)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 10, color: "var(--text-muted)" }}>
                          {a.job?.domain || '—'} · ${rate}/hr · {(a.hoursLogged || 0).toFixed(0)}h logged
                          {periodHrsForThis > 0 && <span style={{ color: "var(--text-success)" }}> · {periodHrsForThis.toFixed(1)}h this period</span>}
                        </div>
                        <span style={{ fontSize: 9, fontWeight: 500, padding: '1px 6px', borderRadius: 3, background: a.status === 'Active' ? 'rgba(93,202,165,0.1)' : a.status === 'Completed' ? 'rgba(156,163,175,0.1)' : 'rgba(245,158,11,0.1)', color: a.status === 'Active' ? 'var(--text-success)' : a.status === 'Completed' ? 'var(--text-muted)' : 'var(--text-warning)' }}>{a.status}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ─── Activity & Time Log (always visible; empty state when no entries) ─── */}
          <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px' }}>Activity</div>
                <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{presetLabel}</div>
              </div>
              {/* Fixed 260px row height so bars don't shift as right-column rows
                  change with the timeframe preset. Both columns have matching
                  inner layout: header pinned top, body flex:1 with scroll if
                  needed. Chart column pins content to the top (not centered)
                  so the bar baseline sits just below the bars instead of
                  floating mid-card. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, height: 260 }}>
                {/* Left: Activity Chart — top-aligned, fixed-size render area */}
                <div style={{ background: "var(--bg-card)", borderRadius: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', overflow: 'hidden' }}>
                  {(() => {
                    const chartEntries = allPeriodEntries.map(e => {
                      const assign = allAssigns.find(a => a.id === e.assignment_id) || allAssigns.find(a => a.job?.id === e.job_id);
                      const rate = rateByAssignment[e.assignment_id] || assign?.rate || 0;
                      const hours = parseFloat(e.hours) || 0;
                      return {
                        date: e.date, hours,
                        jobId: e.assignment_id || e.job_id,
                        jobTitle: assign?.job?.title || e.job?.title || null,
                        earned: hours * rate,
                      };
                    });
                    const colorMap = new Map();
                    for (const a of allAssigns) {
                      if (a.id && a.job?.domain) colorMap.set(a.id, getTagColor(a.job.domain));
                      if (a.job?.id && a.job?.domain) colorMap.set(a.job.id, getTagColor(a.job.domain));
                    }
                    return <ActivityChart entries={chartEntries} mode="stacked" projectColors={colorMap} height={180} days={periodDays} showEarnings />;
                  })()}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 'auto', paddingTop: 8, fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                    <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>{periodHours.toFixed(1)}h</span>
                    <span style={{ color: "var(--text-muted)" }}>{allPeriodEntries.length} entries</span>
                    <span style={{ color: "var(--text-success)", fontWeight: 500 }}>${Math.round(periodEarnings)}</span>
                  </div>
                </div>
                {/* Right: Time Entries — fixed container, rows scroll */}
                <div style={{ background: "var(--bg-card)", borderRadius: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 50px 60px 55px', padding: '8px 12px', fontSize: 9, fontWeight: 500, color: "var(--text-muted)", textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                    <span>Date</span><span>Project</span><span style={{ textAlign: 'right' }}>Hours</span><span style={{ textAlign: 'right' }}>Earned</span><span style={{ textAlign: 'right' }}>Status</span>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                    {[...allPeriodEntries].sort((a, b) => b.date.localeCompare(a.date)).map((entry, i) => {
                      const rate = rateByAssignment[entry.assignment_id] || allAssigns.find(a => a.job?.id === entry.job_id)?.rate || 0;
                      const entryEarned = parseFloat(entry.hours || 0) * rate;
                      const jobTitle = allAssigns.find(a => a.id === entry.assignment_id)?.job?.title || entry.job?.title || '—';
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 50px 60px 55px', padding: '7px 12px', fontSize: 11, borderBottom: '1px solid var(--border-light)' }}>
                          <span style={{ color: "var(--text-muted)", fontSize: 10 }}>{new Date(entry.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                          <span style={{ color: "var(--text-primary)", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{jobTitle}</span>
                          <span style={{ textAlign: 'right', color: "var(--text-muted)", fontFamily: 'var(--font-mono)' }}>{parseFloat(entry.hours).toFixed(1)}h</span>
                          <span style={{ textAlign: 'right', fontWeight: 500, fontFamily: 'var(--font-mono)', color: "var(--text-primary)" }}>${Math.round(entryEarned)}</span>
                          <span style={{ textAlign: 'right', fontSize: 9, fontWeight: 500, color: entry.status === 'approved' ? 'var(--text-success)' : 'var(--text-warning)' }}>{entry.status === 'approved' ? 'Approved' : 'Pending'}</span>
                        </div>
                      );
                    })}
                    {allPeriodEntries.length === 0 && (
                      <div style={{ padding: '40px 16px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                        No activity in {presetLabel.toLowerCase()} — log hours on My Work to see entries here
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          {/* ─── Payment History (only when real payments exist) ─── */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>Payment History</div>
            {!hasRealPayments ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: "var(--text-muted)", background: "var(--bg-card)", borderRadius: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
                Payouts will appear here once processed by your project manager.
              </div>
            ) : (
              <div style={{ background: "var(--bg-card)", borderRadius: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 60px 60px 80px 60px', padding: '8px 16px', fontSize: 9, fontWeight: 500, color: "var(--text-muted)", textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1px solid var(--border)' }}>
                  <span>Date</span><span>Project</span><span style={{ textAlign: 'right' }}>Hours</span><span style={{ textAlign: 'right' }}>Rate</span><span style={{ textAlign: 'right' }}>Total</span><span style={{ textAlign: 'right' }}>Status</span>
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {realTransactions.map(tx => {
                    // Phase 2 audit Finding #5 — reversals are distinct rows.
                    // Negative amounts render in error color; project column gets
                    // a "Reversal" prefix badge so annotators understand the line.
                    const isReversal = tx.type === 'reversal';
                    const total = Number(tx.total ?? 0);
                    const isNegative = total < 0;
                    // 2-decimal display preserves the NUMERIC(12,2) precision we
                    // hardened at the DB layer in migration 213.
                    const totalDisplay = `${isNegative ? '-' : ''}$${Math.abs(total).toFixed(2)}`;
                    // Full status-enum handling (Pending/Processing/Paid/Failed).
                    const statusLower = (tx.status || '').toLowerCase();
                    const statusLabel = statusLower ? statusLower[0].toUpperCase() + statusLower.slice(1) : 'Pending';
                    const statusColor = statusLower === 'paid' ? 'var(--text-success)'
                      : statusLower === 'failed' ? 'var(--text-error)'
                      : statusLower === 'processing' ? 'var(--accent-teal)'
                      : 'var(--text-warning)';
                    const totalColor = isNegative ? 'var(--text-error)' : 'var(--text-primary)';
                    return (
                      <div key={tx.id} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 60px 60px 80px 60px', padding: '8px 16px', fontSize: 11, borderBottom: '1px solid var(--border-light)', background: isReversal ? 'rgba(var(--accent-error-rgb, 220, 53, 69), 0.03)' : 'transparent' }}>
                        <span style={{ color: "var(--text-muted)" }}>{tx.date}</span>
                        <span style={{ color: "var(--text-primary)", display: 'flex', alignItems: 'center', gap: 6 }}>
                          {isReversal && (
                            <span style={{ fontSize: 8, fontWeight: 600, padding: '2px 5px', borderRadius: 3, background: 'var(--bg-error, rgba(220, 53, 69, 0.12))', color: 'var(--text-error)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                              Reversal
                            </span>
                          )}
                          <span>{tx.job || tx.description}</span>
                        </span>
                        <span style={{ textAlign: 'right', color: "var(--text-muted)" }}>{tx.hours ? `${tx.hours}h` : '—'}</span>
                        <span style={{ textAlign: 'right', color: "var(--text-muted)" }}>{tx.baseRate ? `$${Number(tx.baseRate).toFixed(2)}` : '—'}</span>
                        <span style={{ textAlign: 'right', fontWeight: 500, fontFamily: 'var(--font-mono)', color: totalColor }}>{totalDisplay}</span>
                        <span style={{ textAlign: 'right', fontSize: 9, fontWeight: 500, color: statusColor }}>{statusLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
        );
      })()}

      {/* Referrals Tab */}
      {activeTab === 'Referrals' && (() => {
        const refData = dbReferrals || { referrals: [], code: 'LOADING', totalEarnings: 0 };
        const referralBonusAmount = getConfig?.('referral.referee_bonus', 50) || 50;
        const activeRefs = refData.referrals.filter(r => ['SignedUp', 'AssessmentPassed', 'FirstJobCompleted', 'BonusPaid'].includes(r.status));
        const pendingRefs = refData.referrals.filter(r => r.status === 'Invited');
        return (
        <>
          {/* KPI stat row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, marginBottom: 24 }}>
            {[
              { value: refData.referrals.length, label: 'Total Referred' },
              { value: activeRefs.length, label: 'Active' },
              { value: pendingRefs.length, label: 'Pending' },
              { value: `$${currentUser?.total_referral_earnings || refData.totalEarnings || 0}`, label: 'Earned' },
            ].map((kpi, i, arr) => (
              <div key={kpi.label} style={{ textAlign: 'center', padding: '12px 8px', borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontSize: 28, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)", lineHeight: 1 }}>{kpi.value}</div>
                <div style={{ fontSize: 9, fontWeight: 500, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: 6 }}>{kpi.label}</div>
              </div>
            ))}
          </div>

          {/* Referral code */}
          <div style={{ background: "var(--bg-card)", borderRadius: 8, padding: '18px 20px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', marginBottom: 20, borderLeft: '3px solid #5DCAA5' }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)", marginBottom: 6 }}>Your Referral Code</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>
              Share your code with colleagues. Earn <span style={{ color: "var(--text-success)", fontWeight: 500 }}>${referralBonusAmount}</span> when your referral completes their first job.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ padding: '10px 20px', background: "var(--bg-stat)", border: '1px solid var(--border)', borderRadius: 6, fontSize: 16, fontFamily: 'var(--font-mono)', color: "var(--text-primary)", letterSpacing: '2px' }}>
                {currentUser?.referral_code || refData.code || '—'}
              </div>
              <button onClick={() => { navigator.clipboard?.writeText(currentUser?.referral_code || refData.code || ''); }} style={{
                padding: '8px 16px', fontSize: 11, fontWeight: 500, background: "var(--bg)", color: "var(--text-primary)", border: 'none', borderRadius: 6, cursor: 'pointer',
              }}>Copy Code</button>
            </div>
          </div>

          {/* How it works */}
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.48px', marginBottom: 10 }}>How Referrals Work</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
            {[
              { step: '1', title: 'Share Your Code', desc: 'Send your referral code to a colleague in your domain' },
              { step: '2', title: 'They Join Juuni', desc: 'Your referral signs up, completes their profile, and passes an assessment' },
              { step: '3', title: `Earn $${referralBonusAmount}`, desc: `When they complete their first job, you both earn $${referralBonusAmount}` },
            ].map(s => (
              <div key={s.step} style={{ background: "var(--bg-card)", borderRadius: 8, padding: '16px 18px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
                <div style={{ fontSize: 20, fontFamily: 'var(--font-mono)', fontWeight: 600, color: "var(--text-primary)", marginBottom: 8 }}>{s.step}</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", marginBottom: 4 }}>{s.title}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>{s.desc}</div>
              </div>
            ))}
          </div>

          {/* Your referrals — from referral table */}
          <div className="section-title">
            Your Referrals
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            {refData.referrals.map((ref, i) => {
              const milestoneLabels = { 'Invited': 'Invited', 'SignedUp': 'Signed Up', 'AssessmentPassed': 'Assessment Passed', 'FirstJobCompleted': 'First Job Done', 'BonusPaid': 'Bonus Paid', 'Expired': 'Expired' };
              const milestoneColors = { 'Invited': 'var(--accent-gold)', 'SignedUp': '#3b82f6', 'AssessmentPassed': '#8b5cf6', 'FirstJobCompleted': 'var(--accent-teal)', 'BonusPaid': 'var(--accent-teal)', 'Expired': 'var(--text-error)' };
              const displayStatus = milestoneLabels[ref.status] || ref.status;
              const statusColor = milestoneColors[ref.status] || 'var(--text-muted)';
              const milestoneSteps = ['Invited', 'SignedUp', 'AssessmentPassed', 'FirstJobCompleted', 'BonusPaid'];
              const currentStep = milestoneSteps.indexOf(ref.status);
              const earned = ref.referrerBonus && currentStep >= 3 ? `$${ref.referrerBonus}` : '$0';
              return (
              <div key={ref.id || ref.refereeName || `referral-${i}`} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-card)', padding: '14px 20px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)" }}>{ref.refereeName || 'Pending signup'}</span>
                    <span style={{
                      fontSize: 9, fontFamily: 'var(--font-mono)', padding: '2px 8px',
                      borderRadius: 4,
                      background: `${statusColor}12`,
                      color: statusColor,
                    }}>{displayStatus}</span>
                    {/* Milestone progress dots */}
                    {currentStep >= 0 && (
                      <span style={{ display: 'inline-flex', gap: 2, marginLeft: 4 }}>
                        {milestoneSteps.slice(0, 4).map((step, i) => (
                          <span key={`milestone-${step}`} style={{ width: 4, height: 4, borderRadius: '50%', background: i <= currentStep ? statusColor : 'var(--border)' }} />
                        ))}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {ref.refereeDomain || '—'} · Invited {ref.invitedAt ? new Date(ref.invitedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </div>
                </div>
                <div style={{ fontSize: 16, fontFamily: 'var(--font-mono)', fontWeight: 500, color: earned !== '$0' ? 'var(--text-success)' : 'var(--text-muted)' }}>
                  {earned}
                </div>
              </div>
              );
            })}
          </div>

          {/* Referral bonus per job */}
          <div className="section-title">
            Referral Bonuses by Job
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12 }}>
            Refer someone who gets hired on these jobs and earn the listed bonus.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {allJobs.filter(j => j.referralBonus >= 200).slice(0, 6).map(job => (
              <div key={job.id} style={{
                padding: '12px 16px', background: "var(--bg-card)", borderRadius: 8,
                boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}>{job.title}</div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{job.domain}</div>
                </div>
                <div style={{ fontSize: 14, fontFamily: 'var(--font-mono)', fontWeight: 600, color: "var(--text-primary)" }}>${job.referralBonus}</div>
              </div>
            ))}
          </div>
        </>
        );
      })()}
    </div>
  );
}

// ─── Context Panel for Find Work right sidebar ───
function ContextPanel({ qualifiedDomains, applications, savedJobIds, dbSavedJobs, navigate }) {
  const [panelTab, setPanelTab] = useState('qualifications');
  const [qualSearch, setQualSearch] = useState('');

  const allDomains = ['Audio', 'Video', 'Visual', 'Linguistics', 'Music', 'Data Science', 'AI Evaluation', 'Computer Use'];
  const filteredDomains = qualSearch ? allDomains.filter(d => d.toLowerCase().includes(qualSearch.toLowerCase())) : allDomains;

  const appList = Array.isArray(applications) ? applications : [];
  const savedList = dbSavedJobs || [];
  const savedCount = savedList.length || savedJobIds?.size || 0;

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 12 }}>
        {[
          { id: 'qualifications', label: 'qualifications' },
          { id: 'applications', label: `applications (${appList.length})` },
          { id: 'saved', label: `saved (${savedCount})` },
        ].map(tab => (
          <button key={tab.id} onClick={() => setPanelTab(tab.id)} style={{
            padding: '8px 12px', fontSize: 12, fontWeight: 600, textTransform: 'lowercase',
            background: 'none', border: 'none', cursor: 'pointer',
            color: panelTab === tab.id ? 'var(--text-primary)' : 'var(--text-muted)',
            borderBottom: panelTab === tab.id ? '2px solid var(--text-primary)' : '2px solid transparent',
          }}>{tab.label}</button>
        ))}
      </div>

      {/* Qualifications tab */}
      {panelTab === 'qualifications' && (
        <div>
          <input
            type="text" placeholder="Search domains..." value={qualSearch} onChange={e => setQualSearch(e.target.value)}
            style={{ width: '100%', padding: '6px 10px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 4, background: "var(--bg-card)", color: "var(--text-primary)", outline: 'none', marginBottom: 10, boxSizing: 'border-box' }}
          />
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          {filteredDomains.map(domain => {
            const qualified = qualifiedDomains?.has(domain);
            return (
              <div key={domain} onClick={() => navigate('/assessments')} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 0', borderBottom: '1px solid var(--border-light)', cursor: 'pointer',
                transition: 'background 0.1s', borderRadius: 4, margin: '0 -4px', paddingLeft: 4, paddingRight: 4,
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-stat)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 12, color: "var(--text-primary)" }}>{domain}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 500, color: qualified ? 'var(--text-success)' : 'var(--text-muted)' }}>
                    {qualified ? 'Qualified' : 'Not assessed'}
                  </span>
                  <span style={{ fontSize: 10, color: "var(--text-muted)" }}>→</span>
                </div>
              </div>
            );
          })}
          </div>
          <div style={{ marginTop: 12 }}>
            <button onClick={() => navigate('/assessments')} style={{
              width: '100%', padding: '8px 0', fontSize: 11, fontWeight: 500,
              background: 'transparent', color: "var(--text-primary)", border: '1px solid var(--border)',
              borderRadius: 6, cursor: 'pointer',
            }}>Take Assessments →</button>
          </div>
        </div>
      )}

      {/* Applications tab */}
      {panelTab === 'applications' && (
        <div>
          {appList.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 11, color: "var(--text-muted)" }}>No applications yet</div>
          ) : appList.slice(0, 10).map((app, i) => {
            const stageColor = app.stage === 'Accepted' ? 'var(--text-success)' : app.stage === 'Not Selected' ? 'var(--text-error)' : 'var(--text-muted)';
            return (
              <div key={app.id || i} onClick={() => app.jobId && navigate(`/job/${app.jobId}`)}
                style={{ padding: '8px 0', borderBottom: '1px solid var(--border-light)', cursor: 'pointer' }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}>{app.jobTitle || 'Unknown'}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                  <span style={{ fontSize: 10, color: "var(--text-muted)" }}>{app.domain || ''}</span>
                  <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 500, color: stageColor }}>{app.stageLabel || app.stage || '—'}</span>
                </div>
              </div>
            );
          })}
          <div style={{ marginTop: 12 }}>
            <button onClick={() => navigate('/my-work')} style={{
              width: '100%', padding: '8px 0', fontSize: 11, fontWeight: 500,
              background: 'transparent', color: "var(--text-primary)", border: '1px solid var(--border)',
              borderRadius: 6, cursor: 'pointer',
            }}>View All Applications →</button>
          </div>
        </div>
      )}

      {/* Saved tab */}
      {panelTab === 'saved' && (
        <div>
          {savedCount === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 11, color: "var(--text-muted)" }}>No saved jobs</div>
          ) : savedList.slice(0, 10).map((job, i) => (
            <div key={job.id || i} onClick={() => job.id && navigate(`/job/${job.id}`)}
              style={{ padding: '8px 0', borderBottom: '1px solid var(--border-light)', cursor: 'pointer' }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}>{job.title || 'Saved Job'}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                <span style={{ fontSize: 10, color: "var(--text-muted)" }}>{job.domain || ''}</span>
                <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: "var(--text-primary)" }}>{job.rate ? `$${job.rate}/hr` : ''}</span>
              </div>
            </div>
          ))}
          <div style={{ marginTop: 12 }}>
            <button onClick={() => navigate('/my-work')} style={{
              width: '100%', padding: '8px 0', fontSize: 11, fontWeight: 500,
              background: 'transparent', color: "var(--text-primary)", border: '1px solid var(--border)',
              borderRadius: 6, cursor: 'pointer',
            }}>View All Saved →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Saved Jobs Tab with search + domain filter ───
function SavedJobsTab({ savedJobs, dbSavedJobs, allJobs, toggleSaveJob, navigate, assignedJobIds = new Set() }) {
  const [savedSearch, setSavedSearch] = useState('');
  const [savedDomain, setSavedDomain] = useState('All');

  const savedList = (dbSavedJobs || allJobs || []).filter(j => savedJobs.has(j.id));
  const domains = ['All', ...new Set(savedList.map(j => j.domain).filter(Boolean))];
  const filtered = savedList
    .filter(j => savedDomain === 'All' || j.domain === savedDomain)
    .filter(j => !savedSearch || j.title?.toLowerCase().includes(savedSearch.toLowerCase()));

  if (savedJobs.size === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 8 }}>No saved jobs yet</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Click the bookmark icon on any job to save it for later</div>
      </div>
    );
  }

  return (
    <>
      {/* Search + domain filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text" placeholder="Search saved jobs..." value={savedSearch} onChange={e => setSavedSearch(e.target.value)}
          style={{ flex: 1, padding: '8px 12px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, background: "var(--bg-card)", color: "var(--text-primary)", outline: 'none' }}
          onFocus={e => { e.target.style.borderColor = 'var(--text-success)'; }}
          onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
        />
        {domains.length > 2 && (
          <select value={savedDomain} onChange={e => setSavedDomain(e.target.value)}
            style={{ padding: '8px 12px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 6, background: "var(--bg-card)", color: "var(--text-primary)", outline: 'none', cursor: 'pointer' }}>
            {domains.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        <span style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: 'nowrap' }}>{filtered.length} of {savedList.length}</span>
      </div>

      {/* Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        {filtered.map(job => (
          <div key={job.id} onClick={() => navigate(`/job/${job.id}`)}
            style={{
              background: "var(--bg-card)", border: '1px solid var(--border)', borderRadius: 8, padding: 14,
              boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', cursor: 'pointer', transition: 'box-shadow 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.08) 0px 2px 8px'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.04) 0px 1px 3px'; }}
          >
            {/* Title + unsave */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>{job.title}</span>
              <span onClick={e => { e.stopPropagation(); toggleSaveJob(job.id); }} style={{ cursor: 'pointer', flexShrink: 0 }}>
                <svg width="12" height="14" viewBox="0 0 14 16" fill="var(--accent-teal)" stroke="var(--accent-teal)" strokeWidth="1.5"><path d="M1.5 1.5H12.5V14.5L7 11L1.5 14.5V1.5Z"/></svg>
              </span>
            </div>
            {/* Domain + status */}
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{job.domain}{job.duration ? ` · ${job.duration}` : ''}</span>
              {assignedJobIds.has(job.id) && <span style={{ fontSize: 8, fontWeight: 600, padding: '1px 5px', borderRadius: 3, background: 'var(--bg-success)', color: 'var(--text-success)' }}>Assigned</span>}
            </div>
            {/* Rate + spots */}
            <div style={{ display: 'flex', gap: 16, fontSize: 12, fontFamily: 'var(--font-mono)', color: "var(--text-primary)" }}>
              <span>${job.rate}/hr</span>
              <span>{job.currentStaffed || 0}/{job.teamSize || '?'} spots</span>
            </div>
          </div>
        ))}
      </div>
      {filtered.length === 0 && savedList.length > 0 && (
        <div style={{ padding: '24px', textAlign: 'center', fontSize: 12, color: "var(--text-muted)" }}>No saved jobs match your filters</div>
      )}
    </>
  );
}

// ─── Weekly Trend Chart with hover tooltips ───
function WeeklyTrendChart({ data = [] }) {
  const [hovered, setHovered] = useState(null);
  const maxVal = Math.max(...data, 1);
  const now = new Date();

  return (
    <div style={{ padding: '8px 16px', textAlign: 'center' }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 48, position: 'relative', marginBottom: 6 }}>
        {data.map((val, i) => {
          const weeksAgo = data.length - 1 - i;
          const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - weeksAgo * 7);
          const weekStart = new Date(weekEnd.getTime() - 7 * 86400000);
          const periodLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, position: 'relative' }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              {hovered === i && (
                <div style={{
                  position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                  marginBottom: 6, padding: '6px 10px', background: "var(--bg)", color: "var(--text-primary)", borderRadius: 6,
                  fontSize: 10, whiteSpace: 'nowrap', zIndex: 10, textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}>
                  <div style={{ fontWeight: 600 }}>${val.toLocaleString()}</div>
                  <div style={{ fontSize: 8, color: "var(--text-muted)", marginTop: 2 }}>{periodLabel}</div>
                </div>
              )}
              <div style={{
                width: '100%', borderRadius: 3,
                height: val > 0 ? Math.max(6, (val / maxVal) * 40) : 4,
                background: val > 0 ? 'var(--text-success)' : 'var(--border)',
                opacity: hovered === i ? 1 : (val > 0 ? 0.7 : 0.3),
                cursor: 'pointer',
                transition: 'opacity 0.15s',
              }} />
              <span style={{ fontSize: 8, color: "var(--text-muted)" }}>
                {val > 0 ? `$${val >= 1000 ? (val / 1000).toFixed(1) + 'K' : val}` : '—'}
              </span>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 9, fontWeight: 500, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px' }}>Weekly Earnings</div>
    </div>
  );
}

